import axios, { AxiosInstance } from 'axios';
import type {
  Application,
  ApplicationDetail,
  ProduktVorgaben,
  ProductApplicationLink,
  ZustandsBefund,
  User,
  UserRole,
  UserStatus,
  Customer,
  CustomerUser,
  CustomerRole,
  Product,
  ProductPlan,
  ComposeTemplate,
  Order,
  OrderStatus,
  Instance,
  InstanceSecret,
  Server,
  DeploymentJob,
  Release,
  AuditLog,
  MfaStatus,
  MfaSetup,
  EmbedKey,
  PendingSignup,
  PaginatedResponse,
  ActivationState,
  InstanceNotice,
  InstanceHeartbeat,
  SelfHostedInstance,
  NoticeSeverity,
  NoticeAudience,
} from './types';

// ─── Axios instance ────────────────────────────────────────────────────────────

const client: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────────────

export interface LoginResult {
  token?: string;
  user?: User;
  /** Password accepted, but a second factor is still outstanding. */
  mfaRequired?: boolean;
  challenge?: string;
}


/**
 * Holt eine Liste **vollstaendig**, ueber alle Seiten des Backends hinweg.
 *
 * Das Backend schneidet jede Liste bei 20 oder 50 Zeilen ab und legt die
 * Gesamtzahl in `pagination` daneben. Die Aufrufe hier haben davon nur das
 * Array genommen (`data.data ?? data`) und den Umschlag weggeworfen -- damit
 * zeigte jede Tabelle stillschweigend die ersten 20 Eintraege und sah aus, als
 * waere das alles. Genau so entstand "ich komme nur bis bookloox.de".
 *
 * Vollstaendig zu laden ist hier die richtige Wahl, weil Suche und Sortierung
 * im Browser laufen (siehe TabellenSteuerung): eine Suche ueber eine halbe
 * Liste antwortet "nichts gefunden", und das sieht aus wie eine Antwort.
 *
 * Wer selbst ein `limit` mitgibt, will ausdruecklich einen Ausschnitt -- etwa
 * das Dashboard mit den letzten fuenf Auftraegen. Dann wird genau eine Abfrage
 * gestellt.
 *
 * Nicht fuer Verlaeufe, die unbegrenzt wachsen. Das Audit-Log blaettert
 * deshalb serverseitig weiter.
 */
async function alleSeiten<T>(pfad: string, params?: Record<string, unknown>): Promise<T[]> {
  const erste = await client.get(pfad, { params });
  const auspacken = (roh: any): T[] =>
    Array.isArray(roh) ? roh : (roh?.data ?? roh?.domains ?? []);

  const rumpf = erste.data;
  const zeilen = auspacken(rumpf);

  // Kein Umschlag, ein ausdruecklicher Ausschnitt oder nur eine Seite: fertig.
  const seiten: number = rumpf?.pagination?.pages ?? 1;
  if (Array.isArray(rumpf) || params?.limit || seiten <= 1) return zeilen;

  const limit: number = rumpf.pagination.limit;
  const weitere = await Promise.all(
    Array.from({ length: seiten - 1 }, (_, i) =>
      client.get(pfad, { params: { ...params, page: i + 2, limit } }).then((r) => auspacken(r.data)),
    ),
  );
  return zeilen.concat(...weitere);
}

export const auth = {
  login: async (email: string, password: string): Promise<LoginResult> => {
    const { data } = await client.post('/auth/login', { email, password });
    return data;
  },
  verifyMfa: async (challenge: string, code: string): Promise<{ token: string; user: User }> => {
    const { data } = await client.post('/auth/2fa/verify', { challenge, code });
    return data;
  },
  mfaStatus: async (): Promise<MfaStatus> => {
    const { data } = await client.get('/auth/2fa/status');
    return data;
  },
  mfaSetup: async (): Promise<MfaSetup> => {
    const { data } = await client.post('/auth/2fa/setup');
    return data;
  },
  mfaEnable: async (code: string): Promise<{ backupCodes: string[] }> => {
    const { data } = await client.post('/auth/2fa/enable', { code });
    return data;
  },
  mfaDisable: async (password: string, code: string): Promise<{ message: string }> => {
    const { data } = await client.post('/auth/2fa/disable', { password, code });
    return data;
  },
  me: async (): Promise<User> => {
    const { data } = await client.get('/auth/me');
    return data;
  },
  /** Was sich die Oberfläche für die angemeldete Person merkt (Filter, Reiter …). */
  einstellungen: async (): Promise<Record<string, unknown>> => {
    const { data } = await client.get('/auth/me/einstellungen');
    return data;
  },
  /** Einzelne Schlüssel setzen; `null` entfernt einen. */
  einstellungenSetzen: async (aenderung: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const { data } = await client.put('/auth/me/einstellungen', aenderung);
    return data;
  },
};

// ─── Users ──────────────────────────────────────────────────────────────────────

export const users = {
  list: async (): Promise<User[]> => {
    const { data } = await client.get('/users');
    return data;
  },
  get: async (id: string): Promise<User> => {
    const { data } = await client.get(`/users/${id}`);
    return data;
  },
  create: async (payload: {
    email: string;
    name: string;
    role: UserRole;
    password?: string;
  }): Promise<User> => {
    const { data } = await client.post('/users', payload);
    return data;
  },
  update: async (
    id: string,
    payload: { name?: string; role?: UserRole; status?: UserStatus; slackId?: string | null },
  ): Promise<User> => {
    const { data } = await client.put(`/users/${id}`, payload);
    return data;
  },
  /** Slack-Kennung fuer EINEN Benutzer nachschlagen. */
  slackIdHolen: async (
    id: string,
  ): Promise<{
    slackId: string | null;
    gefunden: boolean;
    gepruefteAdresse?: string | null;
    vorschlaege?: Array<{ id: string; name: string; email: string | null }>;
  }> => {
    const { data } = await client.post(`/users/${id}/slack-id-holen`);
    return data;
  },
  /** Slack-Kennungen fuer alle Benutzer ohne eine nachschlagen lassen. */
  slackIdsHolen: async (): Promise<{
    geprueft: number;
    gefunden: number;
    ohne: Array<{ name: string; email: string }>;
  }> => {
    const { data } = await client.post('/users/slack-ids-holen');
    return data;
  },
  suspend: async (id: string, reason?: string): Promise<User> => {
    const { data } = await client.post(`/users/${id}/suspend`, { reason });
    return data;
  },
  reactivate: async (id: string): Promise<User> => {
    const { data } = await client.post(`/users/${id}/reactivate`);
    return data;
  },
  sendPasswordReset: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.post(`/users/${id}/send-password-reset`);
    return data;
  },
  activity: async (id: string): Promise<AuditLog[]> => {
    const { data } = await client.get(`/users/${id}/activity`);
    return data;
  },
  reset2fa: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.post(`/users/${id}/reset-2fa`);
    return data;
  },
  changePassword: async (id: string, currentPassword: string, newPassword: string): Promise<void> => {
    await client.post(`/users/${id}/change-password`, { currentPassword, newPassword });
  },
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const { data } = await client.post('/auth/forgot-password', { email });
    return data;
  },
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const { data } = await client.post('/auth/reset-password', { token, password });
    return data;
  },
};

// ─── Products ─────────────────────────────────────────────────────────────────

export const applications = {
  list: async (): Promise<Application[]> => {
    const { data } = await client.get('/applications');
    return data;
  },
  get: async (id: string): Promise<ApplicationDetail> => {
    const { data } = await client.get(`/applications/${id}`);
    return data;
  },
  update: async (id: string, payload: { name?: string; description?: string }): Promise<Application> => {
    const { data } = await client.patch(`/applications/${id}`, payload);
    return data;
  },
  /** Neue Application von Hand (ohne Produkt). Der Slug bleibt danach fest. */
  create: async (payload: { slug: string; name: string; description?: string }): Promise<Application> => {
    const { data } = await client.post('/applications', payload);
    return data;
  },
  // Vorlagen gehoeren seit Stufe 2 der Application.
  getTemplates: async (id: string): Promise<ComposeTemplate[]> => {
    const { data } = await client.get(`/applications/${id}/templates`);
    return data;
  },
  images: async (
    id: string,
    templateId?: string,
  ): Promise<{ template: { id: string; version: string } | null; images: ProduktBild[] }> => {
    const { data } = await client.get(`/applications/${id}/images`, {
      params: templateId ? { templateId } : undefined,
    });
    return data;
  },
  pruefeTemplate: async (id: string, composeTemplate: string, envTemplate: string): Promise<VorlagenBefund> => {
    const { data } = await client.post(`/applications/${id}/templates/pruefen`, { composeTemplate, envTemplate });
    return data;
  },
  createTemplate: async (id: string, payload: Partial<ComposeTemplate>): Promise<ComposeTemplate> => {
    const { data } = await client.post(`/applications/${id}/templates`, payload);
    return data;
  },
};

export const products = {
  list: async (): Promise<Product[]> => {
    const { data } = await client.get('/products');
    return data;
  },
  get: async (id: string): Promise<Product> => {
    const { data } = await client.get(`/products/${id}`);
    return data;
  },
  create: async (payload: Partial<Product>): Promise<Product> => {
    const { data } = await client.post('/products', payload);
    return data;
  },
  update: async (id: string, payload: Partial<Product>): Promise<Product> => {
    const { data } = await client.patch(`/products/${id}`, payload);
    return data;
  },
  setWooCommerceVisibility: async (
    id: string,
    visibility: 'PUBLISH' | 'DRAFT' | 'PRIVATE' | null,
  ): Promise<{ ok: boolean; error?: string; product: Product }> => {
    const { data } = await client.post(`/products/${id}/woocommerce/visibility`, { visibility });
    return data;
  },
  delete: async (id: string): Promise<void> => {
    await client.delete(`/products/${id}`);
  },
  /** Loescht ein Produkt restlos. Lehnt ab, sobald eine Instanz daran haengt. */
  purge: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/products/${id}?purge=true`);
    return data;
  },
  getPlans: async (id: string): Promise<ProductPlan[]> => {
    const { data } = await client.get(`/products/${id}/plans`);
    return data;
  },
  createPlan: async (id: string, payload: Partial<ProductPlan>): Promise<ProductPlan> => {
    const { data } = await client.post(`/products/${id}/plans`, payload);
    return data;
  },
  updatePlan: async (id: string, planId: string, payload: Partial<ProductPlan>): Promise<ProductPlan> => {
    const { data } = await client.patch(`/products/${id}/plans/${planId}`, payload);
    return data;
  },
  getTemplates: async (id: string): Promise<ComposeTemplate[]> => {
    const { data } = await client.get(`/products/${id}/templates`);
    return data;
  },
  /** Welche Applications das Produkt verkauft, in Reihenfolge, mit Vorgaben je Plan. */
  setApplications: async (
    id: string,
    liste: Array<{ applicationId: string; vorgaben?: ProduktVorgaben }>,
  ): Promise<ProductApplicationLink[]> => {
    const { data } = await client.put(`/products/${id}/applications`, liste);
    return data;
  },
  // Welche eigenen Images ein Produkt faehrt — aus seiner Vorlage gelesen.
  images: async (
    id: string,
    templateId?: string,
  ): Promise<{ template: { id: string; version: string } | null; images: ProduktBild[] }> => {
    const { data } = await client.get(`/products/${id}/images`, {
      params: templateId ? { templateId } : undefined,
    });
    return data;
  },
  // Vorlage pruefen, ohne sie zu speichern.
  pruefeTemplate: async (
    id: string,
    composeTemplate: string,
    envTemplate: string,
  ): Promise<VorlagenBefund> => {
    const { data } = await client.post(`/products/${id}/templates/pruefen`, {
      composeTemplate,
      envTemplate,
    });
    return data;
  },
  createTemplate: async (id: string, payload: Partial<ComposeTemplate>): Promise<ComposeTemplate> => {
    const { data } = await client.post(`/products/${id}/templates`, payload);
    return data;
  },
};

// ─── Servers ──────────────────────────────────────────────────────────────────

/** Was das AWS-Kaufformular braucht — in einer Antwort, damit es nicht dreimal fragt. */
export interface AwsOptions {
  instanceTypes: { name: string; vcpu: number; ramGb: number; approxEurMonth: number; eignung: string }[];
  regions: { id: string; label: string }[];
  defaultRegion: string;
  /** Falsch, wenn die hinterlegten Zugangsdaten nicht tragen — dann steht der Grund daneben. */
  credentialsOk: boolean;
  credentialsError: string | null;
}

export interface GeneratedKeyPair {
  privateKey: string;
  publicKey: string;
  fingerprint: string;
  keyType: string;
  note: string;
}

export interface DockerContainer {
  id: string;
  name: string;
  image: string;
  status: string;
  state: string;
  health: string | null;
  createdAt: string | null;
  runningFor: string | null;
  ports: string[];
  networks: string[];
  composeProject: string | null;
  composeService: string | null;
  restartCount: number | null;
  managedByPc: boolean;
}

export interface DockerHostInfo {
  dockerVersion: string | null;
  containersRunning: number | null;
  containersStopped: number | null;
  images: number | null;
  uptime: string | null;
  loadAverage: string | null;
  memoryUsedMb: number | null;
  memoryTotalMb: number | null;
  diskUsedPercent: number | null;
  diskAvailable: string | null;
  /** Leer auf jedem Host ohne Rechenkarte — der Normalfall. */
  gpus: { model: string; vramMb: number | null }[];
  /** Kann Docker sie durchreichen? Null, wenn es gar keine gibt. */
  gpuDockerAccess: boolean | null;
}

export interface DockerOverview {
  host: DockerHostInfo;
  containers: DockerContainer[];
  warnings: string[];
  collectedAt: string;
}

// ─── Messwerte und Alarme ─────────────────────────────────────────────────────

export interface MetrikPunkt {
  /** Unix-Sekunden. */
  t: number;
  /** `null` heißt: dafür liegt kein Messwert vor. Keine Null. */
  v: number | null;
}

export interface MetrikReihe {
  key: string;
  label: string;
  einheit: 'bytes' | 'prozent' | 'anzahl' | 'sekunden' | 'proSekunde';
  punkte: MetrikPunkt[];
}

export interface MetrikZustand {
  verfuegbar: boolean;
  /** Im Klartext, warum nicht. Gehört angezeigt, nicht verschluckt. */
  grund?: string;
  speicher?: 'roh' | 'verlauf';
}

export interface MetrikAntwort {
  zustand: MetrikZustand;
  reihen: MetrikReihe[];
}

export type Zeitraum = '1h' | '6h' | '24h' | '7d' | '30d' | '90d' | '1y' | '3y';

export interface Alarm {
  id: string;
  name: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'firing' | 'resolved';
  summary: string;
  description: string | null;
  labels: Record<string, string>;
  serverId: string | null;
  instanceId: string | null;
  startsAt: string;
  endsAt: string | null;
  silencedUntil: string | null;
  silencedBy: string | null;
  server?: { hostname: string } | null;
  instance?: { stackName: string | null } | null;
}

export interface WirtUebersicht {
  serverId: string;
  hostname: string;
  ipAddress: string;
  status: string;
  /** „hostinger", „aws", „manual" … — für die Filterleiste. */
  provider: string;
  /** Der Kunde, dem der Server gehört; null bei unseren eigenen. */
  kunde: string | null;
  /** Treffen Messwerte ein? Nicht dasselbe wie der Status in der Serverliste. */
  online: boolean;
  cpuProzent: number | null;
  /** Vom Anbieter entzogene Rechenzeit. Ueber ~20 % heisst: gedrosselt. */
  stealProzent: number | null;
  ramBelegtBytes: number | null;
  ramGesamtBytes: number | null;
  plattenProzent: number | null;
  cpuVerlauf: number[];
  gpu: { modell: string | null; vramMb: number | null; anzahl: number | null } | null;
}

export const metrics = {
  /** Alle Wirte auf einen Blick — für den Reiter „Monitoring". */
  wirte: async (): Promise<{ verfuegbar: boolean; grund?: string; wirte: WirtUebersicht[] }> => {
    const { data } = await client.get('/metrics/wirte');
    return data;
  },

  /** Ob überhaupt ein Speicher hinterlegt ist. Vor dem ersten Graphen fragen. */
  status: async (): Promise<{ roh: boolean; verlauf: boolean; zeitraeume: Zeitraum[] }> => {
    const { data } = await client.get('/metrics/status');
    return data;
  },
  instance: async (id: string, zeitraum: Zeitraum): Promise<MetrikAntwort> => {
    const { data } = await client.get(`/metrics/instance/${id}`, { params: { zeitraum } });
    return data;
  },
  server: async (id: string, zeitraum: Zeitraum): Promise<MetrikAntwort> => {
    const { data } = await client.get(`/metrics/server/${id}`, { params: { zeitraum } });
    return data;
  },
  inference: async (zeitraum: Zeitraum): Promise<MetrikAntwort> => {
    const { data } = await client.get('/metrics/inference', { params: { zeitraum } });
    return data;
  },
};

// ─── Webseiten-Statistik ─────────────────────────────────────────────────────
//
// Cookielos erhobene Reichweite der Webseiten-Instanzen. Die Zeitraeume sind
// bewusst andere als bei den Messwerten: hier gibt es Tageswerte, keine
// Stundenaufloesung.

export type StatZeitraum = 'heute' | 'gestern' | '7d' | '28d' | '90d' | '12m';

export interface StatEinbau {
  gepruefetAm: string | null;
  befund: string | null;
  tagGefunden: boolean;
  tagFehltSeit: string | null;
  datenschutzGefunden: boolean;
  datenschutzFehltSeit: string | null;
  datenschutzUrl: string | null;
}

export interface StatStatus {
  verfuegbar: boolean;
  grund?: string;
  ersterTag?: string | null;
  letzterTag?: string | null;
  zuletztGesehenAm?: string | null;
  /** Stand der woechentlichen Nachschau auf der Webseite selbst. */
  einbau?: StatEinbau;
}

export interface StatKennzahlen {
  aufrufe: number;
  besuche: number;
  /** Nicht gezaehlt: Bots und Adressen aus der Ausschlussliste. */
  botsVerworfen: number;
  adressenVerworfen: number;
  aufrufeJeBesuch: number | null;
  einzelseitenAnteil: number | null;
  dauerSchnittMs: number | null;
}

export interface StatUebersicht {
  zeitraum: StatZeitraum;
  von: string;
  bis: string;
  tage: number;
  jetzt: StatKennzahlen;
  vorher: StatKennzahlen;
  verlauf: { tag: string; aufrufe: number; besuche: number }[];
}

export interface StatSeite {
  pfad: string;
  titel: string | null;
  passtInsFenster: boolean;
  aufrufe: number;
  besuche: number;
  einstiege: number;
  ausstiege: number;
  ausstiegeMitAktion: number;
  fehler404: number;
  dauerSchnittMs: number | null;
  dauerProben: number;
  /** Anteil der **gemessenen** Aufrufe je Marke. `null`, wenn nichts gemessen wurde. */
  scroll: { proben: number; stufe25: number; stufe50: number; stufe75: number; stufe100: number } | null;
}

export interface StatAbgleich {
  /** Steht in der Sitemap, wurde im Zeitraum nicht aufgerufen. */
  ohneAufrufe: { pfad: string; entfernt: boolean }[];
  /** Wird aufgerufen, steht in keiner Sitemap — meist die nuetzlichere Liste. */
  ohneSitemap: { pfad: string; aufrufe: number }[];
  sitemapStand: string | null;
}

export interface StatMerkmalZeile {
  wert: string;
  aufrufe: number;
  besuche: number;
}

export interface StatEinrichtung {
  moeglich: boolean;
  grund?: string;
  eingeschaltet?: boolean;
  kennung?: string | null;
  erlaubteHosts?: string[];
  scriptZeile?: string;
  datenschutzText?: string;
  erkennung?: string;
}

export type StatExportArt = 'tage' | 'seiten' | 'merkmale' | 'abgleich';

export type StatMerkmalArt =
  | 'LAND' | 'VERWEISER' | 'GERAET' | 'BROWSER' | 'SYSTEM' | 'KAMPAGNE' | 'QUELLE' | 'MEDIUM';

export const statistik = {
  status: async (id: string): Promise<StatStatus> => {
    const { data } = await client.get(`/statistik/instanz/${id}/status`);
    return data;
  },
  uebersicht: async (id: string, zeitraum: StatZeitraum): Promise<StatUebersicht> => {
    const { data } = await client.get(`/statistik/instanz/${id}/uebersicht`, { params: { zeitraum } });
    return data;
  },
  seiten: async (id: string, zeitraum: StatZeitraum): Promise<StatSeite[]> => {
    const { data } = await client.get(`/statistik/instanz/${id}/seiten`, { params: { zeitraum } });
    return data;
  },
  merkmal: async (id: string, zeitraum: StatZeitraum, art: StatMerkmalArt): Promise<StatMerkmalZeile[]> => {
    const { data } = await client.get(`/statistik/instanz/${id}/merkmal`, { params: { zeitraum, art } });
    return data;
  },
  aktionen: async (
    id: string,
    zeitraum: StatZeitraum,
    art: 'KLICK' | 'AUSGANG' | 'DOWNLOAD',
  ): Promise<{ ziel: string; pfad: string; anzahl: number }[]> => {
    const { data } = await client.get(`/statistik/instanz/${id}/aktionen`, { params: { zeitraum, art } });
    return data;
  },
  abgleich: async (id: string, zeitraum: StatZeitraum): Promise<StatAbgleich> => {
    const { data } = await client.get(`/statistik/instanz/${id}/abgleich`, { params: { zeitraum } });
    return data;
  },
  sitemapLesen: async (id: string): Promise<{ gelesen: number; neu: number; entfernt: number; hinweis?: string }> => {
    const { data } = await client.post(`/statistik/instanz/${id}/sitemap`);
    return data;
  },
  /**
   * Die Zahlen als CSV, immer in Tagesaufloesung.
   *
   * Ueber den Client und nicht ueber einen einfachen Link: der Endpunkt
   * verlangt eine Anmeldung, und ein `<a href>` schickt keinen Kopf mit.
   */
  exportCsv: async (id: string, was: StatExportArt, zeitraum: StatZeitraum): Promise<Blob> => {
    const { data } = await client.get(`/statistik/instanz/${id}/export.csv`, {
      params: { was, zeitraum },
      responseType: 'blob',
    });
    return data;
  },
  einrichtung: async (id: string): Promise<StatEinrichtung> => {
    const { data } = await client.get(`/statistik/instanz/${id}/einrichtung`);
    return data;
  },
  einschalten: async (id: string) => {
    const { data } = await client.post(`/statistik/instanz/${id}/einschalten`, {
      datenschutzBestaetigt: true,
    });
    return data;
  },
  nachschau: async (id: string): Promise<{
    geprueft: boolean;
    befund: string;
    tagGefunden?: boolean;
    datenschutzGefunden?: boolean;
  }> => {
    const { data } = await client.post(`/statistik/instanz/${id}/nachschau`);
    return data;
  },
  ausschalten: async (id: string) => {
    const { data } = await client.post(`/statistik/instanz/${id}/ausschalten`);
    return data;
  },
};

export const alerts = {
  list: async (params?: {
    serverId?: string;
    instanceId?: string;
    offen?: boolean;
  }): Promise<{ alerts: Alarm[]; kanaele: { slack: boolean; email: boolean } }> => {
    const { data } = await client.get('/alerts', {
      params: { ...params, offen: params?.offen ? 'true' : undefined },
    });
    return data;
  },
  /** Befristet — eine Stummschaltung ohne Ende ist abgeschaltete Überwachung. */
  silence: async (id: string, stunden: number): Promise<{ alert: Alarm; message: string }> => {
    const { data } = await client.post(`/alerts/${id}/silence`, { stunden });
    return data;
  },
  unsilence: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/alerts/${id}/silence`);
    return data;
  },
  /** Probenachricht. Ein Kanal, der erst im Ernstfall benutzt wird, ist keiner. */
  test: async (): Promise<{ kanaele: { slack: boolean; email: boolean }; message: string }> => {
    const { data } = await client.post('/alerts/test');
    return data;
  },
};

// ─── Anbieter-Zugänge und Snapshots ───────────────────────────────────────────

/** Was die Oberfläche über einen hinterlegten Zugang erfährt. Nie das Token. */
export interface ProviderCredential {
  id: string;
  provider: string;
  label: string | null;
  createdAt: string;
  rotatedAt: string | null;
  lastUsedAt: string | null;
  lastCheckedAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckError: string | null;
  createdBy: string | null;
}

export interface ProviderCheck {
  ok: boolean;
  servers?: number;
  error?: string;
}

/** Eine dedizierte Maschine, wie Robot sie meldet. */
export interface RobotServer {
  serverNumber: number;
  name: string;
  product: string;
  ipAddress: string | null;
  ipv6Net: string | null;
  datacenter: string;
  traffic: string;
  status: string;
  /** Gekündigt — läuft aus. Gehört sichtbar in die Liste. */
  cancelled: boolean;
  paidUntil: string;
  bereitsImPc: boolean;
}

export interface Snapshot {
  id: string;
  beschreibung: string | null;
  erstellt: string;
  groesseGb: number | null;
  status: string;
  laeuftAbAm?: string | null;
}

/**
 * Ob diese Maschine Snapshots kann — und wenn nicht, warum nicht.
 *
 * `grund` ist für die Anzeige gedacht. Ein abgeblendeter Knopf ohne Begründung
 * lässt den Benutzer raten, und die häufigste Antwort auf „warum geht das
 * nicht" ist hier nicht offensichtlich.
 */
export interface SnapshotFaehigkeit {
  moeglich: boolean;
  grund?: string;
  quelle?: 'customer' | 'house';
  nurEiner: boolean;
  zuruecksetzenMoeglich: boolean;
}

export interface AufraeumBericht {
  vorher: { belegtProzent: number | null; frei: string | null; images: number | null; groesse: string | null };
  nachher: { belegtProzent: number | null; frei: string | null; images: number | null; groesse: string | null };
  freigegeben: string | null;
  buildCache: string | null;
  containerLaufend: number | null;
  nurVorschau: boolean;
}

export const servers = {
  /**
   * What the Docker host is actually running. Opens an SSH connection, so it is
   * slow by API standards — fetch on demand, never on an interval.
   */
  docker: async (id: string): Promise<DockerOverview> => {
    const { data } = await client.get(`/servers/${id}/docker`);
    return data;
  },
  /** Compose-Projekte auf dem Host und ob der PC sie schon fuehrt (SSH, nur auf Verlangen). */
  stacks: async (id: string): Promise<StackKandidat[]> => {
    const { data } = await client.get(`/servers/${id}/stacks`, { timeout: 60_000 });
    return data;
  },
  /** Einen fremden Stack als beobachtete Instanz aufnehmen. Fasst auf dem Host nichts an. */
  stackAufnehmen: async (
    id: string,
    projekt: string,
    body: { productId: string; customerId: string; domain?: string | null },
  ): Promise<{ id: string }> => {
    const { data } = await client.post(`/servers/${id}/stacks/${encodeURIComponent(projekt)}/adopt`, body);
    return data;
  },
  /**
   * Whether the host meets the conditions for deploying to it. Opens an SSH
   * connection, so on demand only — same as `docker` above.
   */
  /**
   * Die dedizierten Maschinen im Robot-Konto dieses Kunden.
   *
   * Eigene Schnittstelle, eigene Anmeldung — ein Cloud-Token trägt dort nicht.
   */
  robotList: async (
    customerId: string,
  ): Promise<{ quelle: 'customer' | 'house'; server: RobotServer[] }> => {
    const { data } = await client.get('/servers/robot', { params: { customerId } });
    return data;
  },

  /** Übernimmt eine davon in den PC. Legt sie nicht an — die gibt es schon. */
  robotImport: async (payload: {
    customerId: string;
    serverNumber: number | string;
    ownership?: 'DEDICATED_JUNI' | 'CUSTOMER_PROVIDED';
    notes?: string;
  }): Promise<{ server: Server; message: string; hinweise: string[] }> => {
    const { data } = await client.post('/servers/robot/import', payload);
    return data;
  },

  /** Nur zur Ansicht — bestellt wird bei dedizierten Maschinen von Hand. */
  robotProducts: async (
    customerId: string,
  ): Promise<{
    products: Array<{
      id: string;
      name: string;
      description: string[];
      priceMonthly: string;
      priceSetup: string;
    }>;
    hinweis: string;
  }> => {
    const { data } = await client.get('/servers/robot/products', { params: { customerId } });
    return data;
  },

  /**
   * Snapshots dieser Maschine, samt Begründung falls es keine geben kann.
   *
   * Reine Anbieter-API — anders als `docker` wird dafür kein SSH geöffnet.
   */
  snapshots: async (
    id: string,
  ): Promise<{ faehigkeit: SnapshotFaehigkeit; snapshots: Snapshot[]; instanzen: number }> => {
    const { data } = await client.get(`/servers/${id}/snapshots`);
    return data;
  },

  /** `ueberschreiben` wirkt nur bei Hostinger — dort gibt es einen Platz. */
  snapshotAnlegen: async (
    id: string,
    description: string,
    ueberschreiben?: boolean,
  ): Promise<{ actionId: string | null; hinweis?: string; message: string }> => {
    const { data } = await client.post(`/servers/${id}/snapshots`, { description, ueberschreiben });
    return data;
  },

  snapshotLoeschen: async (id: string, snapshotId: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/servers/${id}/snapshots/${snapshotId}`);
    return data;
  },

  /**
   * Zurücksetzen — überschreibt die Platte samt aller Instanzen darauf.
   *
   * `instanzenBestaetigt` ist die Zahl, die der Bedienende gesehen hat. Weicht
   * sie von dem ab, was der Server zählt, lehnt das Backend ab. Das ist die
   * Bremse gegen „ich wollte nur diese eine Instanz zurückholen".
   */
  snapshotZuruecksetzen: async (
    id: string,
    snapshotId: string,
    instanzenBestaetigt: number,
  ): Promise<{ actionId: string | null; instanzen: number; message: string }> => {
    const { data } = await client.post(`/servers/${id}/snapshots/${snapshotId}/restore`, {
      instanzenBestaetigt,
    });
    return data;
  },

  /** Next free name from the house convention, plus what is already spoken for. */
  nameSuggestion: async (): Promise<{
    suggestion: string | null;
    taken: { name: string; reason: string }[];
    free: string[];
  }> => {
    const { data } = await client.get('/servers/name-suggestion');
    return data;
  },
  hostCheck: async (id: string): Promise<HostCheckResult> => {
    const { data } = await client.get(`/servers/${id}/host-check`);
    return data;
  },
  /** Repairs one of the conditions the check found. Audited server-side. */
  hostFix: async (id: string, action: FixAction): Promise<FixResult> => {
    const { data } = await client.post(`/servers/${id}/host-fix/${action}`);
    return data;
  },
  /**
   * start / stop / restart only — the API rejects anything that destroys state.
   * `force` is needed to stop a container belonging to the PC itself.
   */
  containerAction: async (
    serverId: string,
    container: string,
    action: 'start' | 'stop' | 'restart',
    force = false,
  ): Promise<{ ok: boolean; output: string; status?: string; error?: string }> => {
    const { data } = await client.post(
      `/servers/${serverId}/docker/${encodeURIComponent(container)}/${action}`,
      { force },
    );
    return data;
  },
  /**
   * Generates an SSH key pair. Nothing is stored yet — the private key comes
   * back once and is persisted only when the server is created with it.
   */
  generateKeypair: async (payload: {
    comment?: string;
    passphrase?: string;
  }): Promise<GeneratedKeyPair> => {
    const { data } = await client.post('/servers/keypair', payload);
    return data;
  },
  list: async (): Promise<Server[]> => {
    const { data } = await client.get('/servers');
    return data;
  },
  get: async (id: string): Promise<Server> => {
    const { data } = await client.get(`/servers/${id}`);
    return data;
  },
  create: async (payload: Partial<Server> & { sshPrivateKey?: string; sshPassphrase?: string }): Promise<Server> => {
    const { data } = await client.post('/servers', payload);
    return data;
  },
  update: async (id: string, payload: Partial<Server>): Promise<Server> => {
    const { data } = await client.patch(`/servers/${id}`, payload);
    return data;
  },
  decommission: async (id: string): Promise<{ message: string; server: Server; warning?: string }> => {
    const { data } = await client.delete(`/servers/${id}`);
    return data;
  },
  /** Loescht einen Datensatz ohne Maschine restlos. Lehnt ab, sobald eine dranhaengt. */
  purge: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/servers/${id}?purge=true`);
    return data;
  },
  getInstances: async (id: string): Promise<Instance[]> => {
    const { data } = await client.get(`/servers/${id}/instances`);
    return data;
  },
  setSshKey: async (id: string, sshPrivateKey: string, sshPassphrase?: string): Promise<void> => {
    await client.put(`/servers/${id}/ssh`, { sshPrivateKey, sshPassphrase });
  },
  deleteSshKey: async (id: string): Promise<void> => {
    await client.delete(`/servers/${id}/ssh`);
  },
  testSsh: async (id: string): Promise<{ success: boolean; output?: string; error?: string; durationMs: number }> => {
    const { data } = await client.post(`/servers/${id}/test-ssh`);
    return data;
  },
  provision: async (payload: ProvisionServerPayload): Promise<{ server: Server; deploymentJobId: string }> => {
    const { data } = await client.post('/servers/provision', payload);
    return data;
  },
  // Read-only provider lookups — no billing impact, used to populate the provisioning form
  hetznerServerTypes: async (): Promise<HetznerServerType[]> => {
    const { data } = await client.get('/servers/hetzner/server-types');
    return data;
  },
  hetznerLocations: async (): Promise<HetznerLocation[]> => {
    const { data } = await client.get('/servers/hetzner/locations');
    return data;
  },
  hostingerDataCenters: async (): Promise<HostingerDataCenter[]> => {
    const { data } = await client.get('/servers/hostinger/data-centers');
    return data;
  },
  hostingerTemplates: async (): Promise<HostingerTemplate[]> => {
    const { data } = await client.get('/servers/hostinger/templates');
    return data;
  },
  awsOptions: async (region?: string): Promise<AwsOptions> => {
    const { data } = await client.get('/servers/aws/options', { params: region ? { region } : undefined });
    return data;
  },
  hostingerPostInstallScripts: async (): Promise<HostingerPostInstallScript[]> => {
    const { data } = await client.get('/servers/hostinger/post-install-scripts');
    return data;
  },
  hostingerCatalogItems: async (category = 'VPS'): Promise<HostingerCatalogItem[]> => {
    const { data } = await client.get('/servers/hostinger/catalog-items', { params: { category } });
    return data;
  },
  /**
   * Alte, ungenutzte Images und den Build-Cache entfernen.
   *
   * `nurVorschau` schaetzt nur, was zu holen waere — praktisch, bevor man es
   * wirklich tut.
   */
  imagesAufraeumen: async (
    id: string,
    opts: { tage?: number; nurVorschau?: boolean } = {},
  ): Promise<AufraeumBericht> => {
    const { data } = await client.post(`/servers/${id}/images-aufraeumen`, opts);
    return data;
  },
};

export interface HetznerPrice {
  location: string;
  price_hourly: { gross: string; net: string };
  price_monthly: { gross: string; net: string };
  included_traffic: number;
  price_per_tb_traffic: { gross: string; net: string };
}

export interface HetznerServerType {
  id: number;
  name: string;
  description: string;
  cores: number;
  memory: number;
  disk: number;
  cpu_type: string;
  architecture: string;
  deprecated: boolean;
  prices: HetznerPrice[];
  locations?: Array<{ id: number; name: string; available: boolean; recommended: boolean }>;
}

export interface HetznerLocation {
  id: number;
  name: string;
  description: string;
  country: string;
  city: string;
}

export interface HostingerDataCenter {
  id: number;
  name: string;
  location: string;
  city: string;
  continent: string;
}

export interface HostingerTemplate {
  id: number;
  name: string;
  description: string;
  documentation: string;
}

export interface HostingerPostInstallScript {
  id: number;
  name: string;
}

export interface HostingerCatalogItem {
  id: string;
  name: string;
  category: string;
  metadata: {
    cpus?: string;
    memory?: string;
    bandwidth?: string;
    disk_space?: string;
    network?: string;
  };
  prices: Array<{
    id: string;
    name: string;
    currency: string;
    price: number;
    first_period_price: number;
    period: number;
    period_unit: string;
  }>;
}

export type ProvisionServerPayload =
  | {
      provider: 'hetzner';
      name: string;
      serverType: string;
      image: string;
      location?: string;
      sshPublicKey?: string;
      sshPrivateKey?: string;
      sshPassphrase?: string;
      maxSlots?: number;
      notes?: string;
    }
  | {
      provider: 'aws';
      name: string;
      instanceType: string;
      region?: string;
      diskGb?: number;
      sshPublicKey?: string;
      sshPrivateKey?: string;
      sshPassphrase?: string;
      maxSlots?: number;
      notes?: string;
    }
  | {
      provider: 'hostinger';
      itemId: string;
      templateId: number;
      dataCenterId: number;
      postInstallScriptId?: number;
      hostname?: string;
      sshPublicKey?: string;
      sshPrivateKey?: string;
      sshPassphrase?: string;
      maxSlots?: number;
      notes?: string;
    };

// ─── Instances ────────────────────────────────────────────────────────────────

export interface InstanceListParams {
  status?: string;
  productId?: string;
  /**
   * Kann die Route seit jeher, stand hier aber nicht.
   *
   * Ohne diesen Eintrag hätte man die Instanzen eines Kunden im Frontend
   * nachträglich aus allen herausgefiltert — bei wachsendem Bestand die Art
   * von Lösung, die eine Weile gutgeht.
   */
  customerId?: string;
  serverId?: string;
  /**
   * MANAGED  -- der PC rollt per SSH aus
   * SELF_HOSTED -- Kundenhardware, die Instanz zieht selbst
   * OBSERVED -- laeuft ueber Portainer, der PC schaut nur zu
   *
   * OBSERVED fehlte hier bis 07.10.2026, obwohl das Backend es laengst kannte.
   * Folge: die Oberflaeche konnte gar nicht darauf pruefen und zeigte Start,
   * Stop, Restart und Deploy auch dort an, wo sie nichts bewirken duerfen.
   */
  hostingMode?: 'MANAGED' | 'SELF_HOSTED' | 'OBSERVED';
  /** Produktinstanz, App oder Webseite — die drei Reiter unter Betrieb. */
  art?: 'PRODUKT' | 'APP' | 'WEBSEITE';
  domain?: string;
  page?: number;
  pageSize?: number;
}

/** Ein Mandant, mit dem eine Instanz starten soll. */
export interface TenantInput {
  /** Leer lassen: dann wird er aus dem Namen abgeleitet. */
  code?: string;
  name: string;
  isPrimary?: boolean;
}

export interface TenantRow extends TenantInput {
  id: string;
  code: string;
  isPrimary: boolean;
  sortOrder: number;
}

/** Was beim nächsten Ausrollen in der .env stünde. */
export interface TenantConfigPreview {
  version: number;
  tenants: { code: string; name: string; primary: boolean }[];
  administrators: {
    email: string;
    tenants: '*' | string[];
    role: 'superadmin' | 'admin';
    /** Module, die dem Kunden trotz Administratorrolle nicht zustehen. */
    denyModules?: string[];
  }[];
  retireUnlisted: boolean;
}

export interface TenantState {
  /** Kennt das Produkt Mandanten? Sonst bleibt der Reiter leer. */
  supported: boolean;
  tenants: TenantRow[];
  retireUnlisted: boolean;
  /** Modulkatalog des Produkts. Leer = keine Freischaltung vorgesehen. */
  availableModules: string[];
  /** Freigeschaltet für diese Instanz. Leer = alles, was das Abbild mitbringt. */
  enabledModules: string[];
  preview: TenantConfigPreview | null;
}

/**
 * Eine Verknüpfung aus Sicht einer Instanz — ojfm bettet open.task oder die
 * eLibrary ein. `rolle` sagt, auf welcher Seite die angefragte Instanz steht,
 * `partner` ist die jeweils andere.
 */
export interface InstanceLink {
  id: string;
  kind: string;
  createdAt: string;
  rolle: 'wirt' | 'gast';
  partner: {
    id: string;
    domain: string | null;
    subdomain: string | null;
    stackName: string | null;
    status: string;
    productSlug: string;
    productName: string;
  };
  /** Die Art kennt keinen gemeinsamen Schlüssel (ojfm ↔ Medusa). */
  ohneSchluessel: boolean;
  hasKey: boolean;
  /** Erste zwölf Zeichen — genug, um ihn in den Logs beider Apps wiederzufinden. */
  keyPrefix: string | null;
}

export interface LinkCandidate {
  id: string;
  domain: string | null;
  subdomain: string | null;
  stackName: string | null;
  product: { slug: string; name: string };
  /** Die Software der Kandidatin — im Bündel das Unterscheidende. */
  application?: { slug: string; name: string } | null;
}

/**
 * Antwort der Aktionen, die einen Hintergrundauftrag anlegen.
 *
 * `deploymentJobId` ist der Schluessel, mit dem sich der Auftrag ueber
 * `deployments.get()` verfolgen laesst.
 */
export interface AuftragAngelegt {
  message: string;
  deploymentJobId: string;
  bullJobId?: string;
}

/**
 * Ein Image, das diese Instanz faehrt, samt der Frage, woher sein Tag kommt.
 *
 * `wirksam` ist der Tag, der beim naechsten Ausrollen wirklich gezogen wird.
 * `instanzTag` und `releaseTag` stehen daneben, damit die Oberflaeche zeigen
 * kann, worauf ein „Zuruecksetzen" fuehrt -- ohne das ist Setzen eine
 * Einbahnstrasse.
 */
export interface InstanceImage {
  dienst: string;
  repository: string;
  composeVariable: string;
  schluessel: string;
  /** Was die Vorlage nimmt, wenn nichts gesetzt ist. `null` = Deploy haelt an. */
  standard: string | null;
  wirksam: string | null;
  herkunft: 'instanz' | 'release' | 'standard' | 'offen';
  instanzTag: string | null;
  releaseTag: string | null;
}

export interface InstanceImageTags {
  tags: { tag: string; createdAt?: string }[];
  /** Gefuellt, wenn die Registry nicht mochte. Dann bleibt Eintippen. */
  grund?: string;
}

/** Ein Befund, den der PC vor dem Ausrollen gefunden hat. */
export interface ImageBefund {
  art: 'KEIN_TAG' | 'TAG_FEHLT_IN_REGISTRY' | 'TAG_VON_HAND' | 'NEUES_IMAGE_IN_VORLAGE';
  /** `haltend` = der Deploy scheitert damit. `hinweis` = laeuft, aber anders als gedacht. */
  schwere: 'haltend' | 'hinweis';
  dienst: string;
  repository: string;
  schluessel: string;
  tag: string | null;
  meldung: string;
}

export interface AusrollBefund {
  /** Nichts zu melden — dann rollt die Oberflaeche ohne Umweg aus. */
  sauber: boolean;
  vorlage: {
    aktuellId: string | null;
    aktuellVersion: string | null;
    neuereId: string | null;
    neuereVersion: string | null;
    veraltet: boolean;
    fehlt: boolean;
  };
  images: ImageBefund[];
  /** Was nicht geprueft werden konnte — „nicht geprueft" statt „in Ordnung". */
  ungeprueft: string[];
}

/** Eine Bildstelle, wie sie aus einer Produktvorlage gelesen wird. */
export interface ProduktBild {
  dienst: string;
  repository: string;
  composeVariable: string;
  schluessel: string;
  standard: string | null;
}

/** Ein Datentraeger der Instanz, wie ihn die Umzugs-Vorpruefung meldet. */
export interface UmzugsPosten {
  kind: 'volume' | 'bind';
  name: string;
  bytes: number | null;
  /**
   * Kommt der SSH-Benutzer heran?
   *
   * Nicht kosmetisch: ein unlesbarer Bind-Mount misst sich als 0 Byte, und ein
   * `tar` darauf erzeugt ein leeres Archiv und meldet Erfolg. Unlesbar ist
   * etwas anderes als leer.
   */
  lesbar: boolean;
}

export interface UmzugsPruefung {
  key: string;
  label: string;
  status: 'PASS' | 'WARN' | 'FAIL';
  value: string | null;
  detail: string | null;
  /** Ein Fehlschlag hier verhindert den Umzug. */
  blocking: boolean;
}

export interface UmzugsPlan {
  instanceId: string;
  stackName: string;
  source: { id: string; hostname: string } | null;
  target: { id: string; hostname: string } | null;
  items: UmzugsPosten[];
  totalBytes: number;
  checks: UmzugsPruefung[];
  ok: boolean;
}

/** Was der Vorlagenpruefer meldet. */
export interface VorlagenBefund {
  ok: boolean;
  befunde: Array<{
    schwere: 'fehler' | 'hinweis';
    art: string;
    text: string;
    zeile?: number;
  }>;
  dienste: string[];
}

/** Was die SQL-Konsole zurueckgibt. */
export interface SqlErgebnis {
  ok: boolean;
  modus: 'lesen' | 'probelauf' | 'schreiben';
  spalten: string[];
  zeilen: string[][];
  gekuerzt: boolean;
  /** Befehlsmarken und Fehlertexte — alles, was keine Ergebnismenge ist. */
  meldung: string;
  dauerMs: number;
}

/** Ein Demo-Inhalt, wie ihn der Katalog fuehrt. */
export interface DemoSaatgut {
  id: string;
  name: string;
  beschreibung: string;
  befehl: string;
  /** Loescht vorhandene Inhalte, bevor er neu anlegt — die Oberflaeche fragt nach. */
  zerstoerend?: boolean;
  letzterLauf?: {
    id: string;
    status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
    startedAt: string | null;
    finishedAt: string | null;
    log: string | null;
  } | null;
}

export const demoSaatgut = {
  /** Katalog eines Produkts — fuer den Anlegedialog, bevor es eine Instanz gibt. */
  katalog: async (produktSlug: string): Promise<DemoSaatgut[]> => {
    const { data } = await client.get(`/demo-saatgut/${produktSlug}`);
    return data.eintraege ?? [];
  },
  /** Katalog einer Instanz, mit dem jeweils letzten Lauf. */
  fuerInstanz: async (
    instanceId: string,
  ): Promise<{ angeboten: boolean; grund: string | null; eintraege: DemoSaatgut[] }> => {
    const { data } = await client.get(`/instances/${instanceId}/demo-saatgut`);
    return data;
  },
  einspielen: async (instanceId: string, seedIds: string[]): Promise<void> => {
    await client.post(`/instances/${instanceId}/demo-saatgut`, { seedIds });
  },
};

/** Was eine zusaetzliche Adresse tun soll. Die fuehrende steht am Instance. */
export type AdressArt = 'GEMAPPT' | 'WEITERLEITUNG';

/** Eine Adresse, unter der eine Instanz erreichbar ist. */
export interface InstanzAdresse {
  id: string;
  hostname: string;
  forwardHost: string;
  forwardPort: number;
  notiz: string | null;
  art: AdressArt;
}

/** Was beim Anklopfen herauskam. Siehe `adressen-pruefung.service.ts`. */
export interface AdressBefund {
  hostname: string;
  ampel: 'gruen' | 'gelb' | 'rot';
  status: number | null;
  ziel: string | null;
  befund: string;
  dauerMs: number;
}

export const instanzAdressen = {
  list: async (
    instanceId: string,
  ): Promise<{
    haupt: string | null;
    weitere: InstanzAdresse[];
    dnsPflege: 'PC' | 'BEIWAGEN' | 'MANUELL' | null;
    server: { hostname: string; ipAddress: string; ipv6Address: string | null } | null;
    domain: string | null;
    subdomain: string | null;
  }> => {
    // Achtung: diese Routen haengen unter `/api/instanzen`, nicht `/api/instances`.
    const { data } = await client.get(`/instanzen/${instanceId}/adressen`);
    return data;
  },
  /**
   * Eine weitere Adresse hinterlegen.
   *
   * `dnsSelbst` entscheidet, ob der PC den DNS-Eintrag gleich setzt. Die
   * Antwort sagt in jedem Fall, was daraus geworden ist — auch wenn es nicht
   * ging, etwa weil die Zone nicht bei uns liegt.
   */
  anlegen: async (
    instanceId: string,
    eingabe: {
      hostname: string;
      forwardHost: string;
      forwardPort?: number;
      notiz?: string;
      dnsSelbst?: boolean;
    },
  ): Promise<{
    id: string;
    hostname: string;
    dns: { versucht: boolean; ok: boolean; meldung: string };
    server: { ipAddress: string; hostname: string } | null;
    hinweis: string;
  }> => {
    const { data } = await client.post(`/instanzen/${instanceId}/adressen`, eingabe);
    return data;
  },

  entfernen: async (instanceId: string, adresseId: string): Promise<{ message?: string }> => {
    const { data } = await client.delete(`/instanzen/${instanceId}/adressen/${adresseId}`);
    return data;
  },

  primaerSetzen: async (instanceId: string, adresseId: string): Promise<{ message?: string }> => {
    const { data } = await client.post(`/instanzen/${instanceId}/adressen/${adresseId}/primaer`);
    return data;
  },

  /** Liefert die Adresse die Seite, oder leitet sie mit 301 auf die fuehrende? */
  artSetzen: async (
    instanceId: string,
    adresseId: string,
    art: AdressArt,
  ): Promise<{ hostname: string; art: AdressArt; ziel: string | null; hinweis: string }> => {
    const { data } = await client.patch(`/instanzen/${instanceId}/adressen/${adresseId}`, { art });
    return data;
  },

  /** Alle Adressen anklopfen — fuer die Ampel auf der Instanzseite. */
  pruefen: async (
    instanceId: string,
  ): Promise<{ geprueftUm: string; fuehrend: string | null; befunde: AdressBefund[] }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/adressen/pruefung`);
    return data;
  },
};

export interface ProxyReparatur {
  id: string;
  ausloeser: string;
  containerName: string;
  erfolg: boolean;
  befundVorher: string | null;
  befundNachher: string | null;
  createdAt: string;
  server: { hostname: string; farbe: string | null };
}

export const proxyWache = {
  verlauf: async (): Promise<{ eintraege: ProxyReparatur[]; abkuehlzeitMinuten: number }> => {
    const { data } = await client.get('/proxy-wache/verlauf');
    return data;
  },
  rundlauf: async (): Promise<{ befunde: Array<{ instanz: string; adresse: string; code: number | null; imProxy: boolean | null; gehandelt: boolean; ergebnis: string }> }> => {
    const { data } = await client.post('/proxy-wache/rundlauf');
    return data;
  },
};

export interface ComposeDienst {
  name: string;
  ports: number[];
  baut: boolean;
}

export const webseiten = {
  /** Welche Dienste und Ports stehen in dieser compose.yaml? */
  composeLesen: async (compose: string): Promise<{ dienste: ComposeDienst[]; einwaende: string[] }> => {
    const { data } = await client.post('/webseiten/compose-lesen', { compose });
    return data;
  },
};

export const dnsPruefung = {
  /** Zeigt dieser Name schon auf uns? */
  pruefen: async (
    hostname: string,
    erwartetV4?: string | null,
    erwartetV6?: string | null,
  ): Promise<{
    hostname: string;
    a: string[];
    aaaa: string[];
    passtV4: boolean | null;
    passtV6: boolean | null;
    hinweis: string | null;
  }> => {
    const { data } = await client.post('/dns-pruefung', {
      hostname,
      ...(erwartetV4 ? { erwartetV4 } : {}),
      ...(erwartetV6 ? { erwartetV6 } : {}),
    });
    return data;
  },
};

export interface FmKunde {
  id: string;
  name: string;
  fm_host: string;
  fm_database: string;
  ojfm_url: string | null;
  ojfm_email: string | null;
}

export const fmMigration = {
  zustand: async (): Promise<{
    erreichbar: boolean;
    grund: string | null;
    kunden: FmKunde[];
    adresse?: string;
  }> => {
    const { data } = await client.get('/fm-migration/zustand');
    return data;
  },
};

// ─── Standards zum Nachschlagen ─────────────────────────────────────────────

export type StandardBereich = 'server' | 'instanz';

export interface StandardEintrag {
  id: string;
  bereich: StandardBereich;
  titel: string;
  inhalt: string;
  reihenfolge: number;
  geaendertVon: string | null;
  updatedAt: string;
}

export const standards = {
  list: async (bereich: StandardBereich): Promise<StandardEintrag[]> => {
    const { data } = await client.get(`/standards/${bereich}`);
    return data;
  },
  create: async (bereich: StandardBereich, b: { titel: string; inhalt: string }): Promise<StandardEintrag> => {
    const { data } = await client.post(`/standards/${bereich}`, b);
    return data;
  },
  update: async (id: string, b: { titel: string; inhalt: string }): Promise<StandardEintrag> => {
    const { data } = await client.put(`/standards/eintrag/${id}`, b);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await client.delete(`/standards/eintrag/${id}`);
  },
  order: async (bereich: StandardBereich, ids: string[]): Promise<StandardEintrag[]> => {
    const { data } = await client.put(`/standards/${bereich}/reihenfolge`, { ids });
    return data;
  },
};

export const instances = {
  list: async (params?: InstanceListParams): Promise<Instance[]> => {
    return alleSeiten<Instance>('/instances', params as Record<string, unknown> | undefined);
  },
  get: async (id: string): Promise<Instance> => {
    const { data } = await client.get(`/instances/${id}`);
    return data;
  },
  create: async (payload: Partial<Instance>): Promise<Instance> => {
    const { data } = await client.post('/instances', payload);
    return data;
  },
  /** Teilaenderung. Die Route ist ein PUT, nimmt aber ein Teilobjekt. */
  update: async (id: string, payload: Partial<Instance>): Promise<Instance> => {
    const { data } = await client.put(`/instances/${id}`, payload);
    return data;
  },
  /**
   * Was die vier Aktionen wirklich zurueckgeben.
   *
   * NICHT den Auftrag selbst -- das stand hier vier Mal als
   * `Promise<DeploymentJob>` und war vier Mal falsch. Die Routen antworten mit
   * `{ message, deploymentJobId, bullJobId }`, und weil ein Rueckgabetyp bei
   * einem `await client.post()` nur behauptet und nicht geprueft wird, hat
   * TypeScript das abgesegnet.
   *
   * Die Folge war nicht sichtbar, sondern still: die Oberflaeche las `job.id`,
   * bekam `undefined` und zeigte deshalb keinen Fortschritt an. Der Auftrag
   * lief korrekt durch -- man sah es nur nirgends.
   *
   * Dieselbe Klasse Fehler wie bei `PaginatedResponse` und bei
   * `DomainCheckResult`: ein Typ, der die Wirklichkeit behauptet statt sie
   * abzubilden, verdeckt den Fehler nicht nur, er segnet ihn ab.
   */
  start: async (id: string): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/start`);
    return data;
  },
  stop: async (id: string): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/stop`);
    return data;
  },
  restart: async (id: string): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/restart`);
    return data;
  },
  /**
   * Nachsehen, was auf dem Wirt wirklich laeuft, und den Zustand nachziehen.
   *
   * Antwortet mit 409, wenn nichts geprüft werden konnte — deshalb hier
   * `validateStatus`: der Befund im Rumpf ist die eigentliche Auskunft, und
   * ein geworfener Fehler würde sie verwerfen.
   */
  zustandPruefen: async (id: string): Promise<ZustandsBefund> => {
    const { data } = await client.post(`/instances/${id}/zustand-pruefen`, undefined, {
      validateStatus: (s) => s === 200 || s === 409,
    });
    return data;
  },
  deploy: async (id: string): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/deploy`);
    return data;
  },
  getLogs: async (id: string): Promise<string> => {
    const { data } = await client.get(`/instances/${id}/logs`);
    return data;
  },
  listSecretKeys: async (id: string): Promise<InstanceSecret[]> => {
    const { data } = await client.get(`/instances/${id}/secrets`);
    return data;
  },
  setSecret: async (id: string, key: string, value: string): Promise<InstanceSecret> => {
    const { data } = await client.put(`/instances/${id}/secrets/${key}`, { value });
    return data;
  },
  deleteSecret: async (id: string, key: string): Promise<void> => {
    await client.delete(`/instances/${id}/secrets/${key}`);
  },
  /**
   * Ein Geheimnis im Klartext — nur ADMIN, nur mit Begründung.
   *
   * Der Wert wird bewusst nicht zwischengespeichert: kein React-Query-Cache,
   * kein Wiederverwenden. Wer ihn ein zweites Mal braucht, fragt ein zweites
   * Mal — und das steht dann auch ein zweites Mal im Protokoll.
   */
  revealSecret: async (id: string, key: string, reason: string): Promise<{ key: string; value: string }> => {
    const { data } = await client.post(`/instances/${id}/secrets/${key}/reveal`, { reason });
    return data;
  },
  getTenants: async (id: string): Promise<TenantState> => {
    const { data } = await client.get(`/instances/${id}/tenants`);
    return data;
  },
  setTenants: async (
    id: string,
    tenants: TenantInput[],
    retireUnlisted?: boolean,
  ): Promise<TenantState> => {
    const { data } = await client.put(`/instances/${id}/tenants`, { tenants, retireUnlisted });
    return data;
  },
  setModules: async (id: string, modules: string[]): Promise<TenantState> => {
    const { data } = await client.put(`/instances/${id}/modules`, { modules });
    return data;
  },
  /** Medusa: Stand des API-Schlüssels für den ojfm-Konnektor (ohne Wert). */
  medusaSchluessel: async (id: string): Promise<{
    vorhanden: boolean;
    praefix: string | null;
    shopUrl: string | null;
    medien: { host: string; bucket: string; secretRef: string; basisUrl: string } | null;
  }> => {
    const { data } = await client.get(`/instances/${id}/medusa-schluessel`);
    return data;
  },
  medusaSchluesselWert: async (id: string): Promise<{ schluessel: string | null }> => {
    const { data } = await client.get(`/instances/${id}/medusa-schluessel/wert`);
    return data;
  },
  /** Erzeugt im laufenden Medusa einen neuen Schlüssel; widerruft den bisherigen. */
  medusaSchluesselErzeugen: async (id: string): Promise<{ schluessel: string; widerrufen: number }> => {
    const { data } = await client.post(`/instances/${id}/medusa-schluessel`, undefined, { timeout: 120_000 });
    return data;
  },
  getLinks: async (id: string): Promise<{ links: InstanceLink[]; candidates: LinkCandidate[] }> => {
    const { data } = await client.get(`/instances/${id}/links`);
    return data;
  },
  createLink: async (id: string, guestInstanceId: string): Promise<{ links: InstanceLink[] }> => {
    const { data } = await client.post(`/instances/${id}/links`, { guestInstanceId });
    return data;
  },
  /**
   * Steht die Verbindung wirklich?
   *
   * Der letzte Schritt stellt eine echte Anfrage an die Gast-Instanz —
   * deshalb auf Verlangen und mit grosszuegiger Zeitgrenze.
   */
  bridgePruefung: async (id: string, linkId: string): Promise<BridgeBefund> => {
    const { data } = await client.get(
      `/instances/${id}/links/${linkId}/pruefung`, { timeout: 120_000 },
    );
    return data;
  },
  rotateLinkKey: async (id: string, linkId: string): Promise<{ keyPrefix: string }> => {
    const { data } = await client.post(`/instances/${id}/links/${linkId}/rotate`);
    return data;
  },
  removeLink: async (id: string, linkId: string): Promise<{ links: InstanceLink[] }> => {
    const { data } = await client.delete(`/instances/${id}/links/${linkId}`);
    return data;
  },

  // ── Zugänge ────────────────────────────────────────────────────────────────
  adminKonten: async (id: string): Promise<{
    master: AdminKonto | null;
    customer: KundenAdminKonto | null;
    /** Wo der Weg in die LAUFENDE Instanz liegt — siehe DirektWeg. */
    direktWeg: DirektWeg;
  }> => {
    const { data } = await client.get(`/instances/${id}/admin`);
    return data;
  },
  /**
   * Adresse ändern und/oder Passwort neu setzen.
   *
   * Das Passwort kommt **einmal** zurück und danach nie wieder — wer es verpasst,
   * setzt ein neues. Ein jederzeit auslesbares Admin-Passwort wäre eine zweite
   * Kopie des Zugangs, die dauerhaft in unserer Datenbank liegt.
   */
  /**
   * Betreiberpasswort in der LAUFENDEN Instanz setzen.
   *
   * `setAdminKonto` schreibt nur in die .env fuer das naechste Ausrollen — ein
   * bestehendes Konto erreicht das nie, weil ojfm ein vorhandenes Passwort beim
   * Start nicht anfasst.
   */
  betreiberPasswortSetzen: async (
    id: string,
    passwort: string,
  ): Promise<{ ok: boolean; meldung: string; getroffen?: number }> => {
    const { data } = await client
      .post(`/instances/${id}/admin/betreiber-passwort`, { passwort })
      .catch((e) => ({ data: e.response?.data ?? { ok: false, meldung: e.message } }));
    return data;
  },
  setAdminKonto: async (
    id: string,
    body: { target: 'master' | 'customer'; email?: string; resetPassword?: boolean },
  ): Promise<{ email: string | null; password?: string; needsDeploy?: boolean }> => {
    const { data } = await client.put(`/instances/${id}/admin`, body);
    return data;
  },
  /** Schickt dem Kundenadministrator einen Link. Siehe `ZugangslinkErgebnis`. */
  zugangslink: async (
    id: string,
    modus: 'anzeigen' | 'setzen',
    /** Probelauf: dieselbe Mail mit echtem Link, aber an die eigene Adresse. */
    anMich = false,
  ): Promise<ZugangslinkErgebnis> => {
    const { data } = await client.post(`/instances/${id}/zugangslink`, { modus, anMich });
    return data;
  },

  // ── Bildtags ───────────────────────────────────────────────────────────────
  images: async (id: string): Promise<InstanceImage[]> => {
    const { data } = await client.get(`/instances/${id}/images`);
    return data.images;
  },
  // Getrennt vom Deploy und rein lesend: fragt nach, was vorher zu wissen waere.
  // Fragt die Registry und kann deshalb ein paar Sekunden brauchen.
  ausrollBefund: async (id: string): Promise<AusrollBefund> => {
    const { data } = await client.get(`/instances/${id}/ausroll-befund`);
    return data;
  },
  imageTags: async (id: string, repository: string): Promise<InstanceImageTags> => {
    const { data } = await client.get(`/instances/${id}/images/tags`, {
      params: { repository },
    });
    return data;
  },
  // `tag: null` entfernt und faellt damit auf das Release zurueck.
  // Vorpruefung fuer den Umzug. Rein lesend -- man darf gefahrlos nachsehen.
  migrationPlan: async (id: string, targetServerId: string): Promise<UmzugsPlan> => {
    const { data } = await client.get(`/instances/${id}/migration`, {
      params: { targetServerId },
    });
    return data;
  },
  // Umzug anstossen. Ab hier steht die Instanz still.
  migrate: async (id: string, targetServerId: string): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/migrate`, { targetServerId });
    return data;
  },

  // SQL gegen die Datenbank der Instanz.
  //
  // `bestaetigung` ist nur beim Schreiben noetig — der Stackname, auch
  // serverseitig geprueft.
  sql: async (
    id: string,
    sql: string,
    modus: 'lesen' | 'probelauf' | 'schreiben',
    bestaetigung?: string,
  ): Promise<SqlErgebnis> => {
    const { data } = await client.post(`/instances/${id}/sql`, { sql, modus, bestaetigung });
    return data;
  },

  // Stack abbauen und die Instanz auf DELETED setzen.
  //
  // `bestaetigung` ist der Stackname und wird auch serverseitig geprueft --
  // was unwiederbringlich ist, darf nicht daran haengen, dass die Oberflaeche
  // mitspielt.
  entfernen: async (
    id: string,
    bestaetigung: string,
    datenLoeschen: boolean,
  ): Promise<AuftragAngelegt> => {
    const { data } = await client.post(`/instances/${id}/entfernen`, {
      bestaetigung,
      datenLoeschen,
    });
    return data;
  },

  // Was in der laufenden Instanz wirklich steht. Wirft nie — ein Grund kommt
  // im Ergebnis mit, damit die Mandantenseite stehen bleibt.
  vorhandeneTenants: async (
    id: string,
  ): Promise<{
    mandanten: { code: string; name: string; isPrimary: boolean; aktiv: boolean }[];
    grund?: string;
  }> => {
    const { data } = await client.get(`/instances/${id}/tenants/vorhanden`);
    return data;
  },
  setImageTag: async (
    id: string,
    key: string,
    tag: string | null,
  ): Promise<{ images: InstanceImage[]; deployNoetig: boolean }> => {
    const { data } = await client.put(`/instances/${id}/images/${key}`, { tag });
    return data;
  },

  // ─── Wenn etwas nicht erreichbar ist ──────────────────────────────────
  //
  // Alle drei oeffnen eine SSH-Verbindung zum Wirt. Sie gehoeren deshalb an
  // einen Knopf und nicht in ein Intervall.

  /** Welche Container zu dieser Instanz gehoeren. */
  container: async (id: string): Promise<InstanzContainerListe> => {
    const { data } = await client.get(`/instances/${id}/container`);
    return data;
  },

  /** Das Protokoll eines ihrer Container — abgerufen, nicht mitlaufend. */
  containerLogs: async (
    id: string,
    container: string,
    zeilen = 300,
  ): Promise<ContainerProtokoll> => {
    const { data } = await client.get(`/instances/${id}/container-logs`, {
      params: { container, zeilen },
      // Zweitausend Zeilen durch eine SSH-Verbindung brauchen laenger als die
      // Standardgrenze, und ein Abbruch sieht hier aus wie ein kaputter Server.
      timeout: 60_000,
    });
    return data;
  },

  /** Wo reisst die Kette von aussen bis zum Container? */
  tlsDiagnose: async (id: string): Promise<TlsDiagnose> => {
    const { data } = await client.get(`/instances/${id}/tls-diagnose`, { timeout: 90_000 });
    return data;
  },

  /**
   * Zertifikat beantragen und an den Proxy-Eintrag haengen — je Adresse der
   * Instanz. Geht auch bei beobachteten Instanzen (fasst nur den NPM an).
   * Let's Encrypt braucht pro Adresse bis zu einer Minute.
   */
  tlsSicherstellen: async (id: string): Promise<TlsSicherstellenErgebnis> => {
    const { data } = await client.post(`/instances/${id}/tls-sicherstellen`, {}, { timeout: 300_000 });
    return data;
  },

  // ─── Notfallzugriff auf eine bryve-Instanz ────────────────────────────
  //
  // Geht ueber SSH zum Wirt und von dort an den Container, nicht von aussen.
  // Deshalb grosszuegige Zeitgrenzen: eine SSH-Verbindung, ein Aufruf im
  // Container, gegebenenfalls noch ein Weg ueber die Datenbank.

  bryveSuperadmins: async (id: string): Promise<{ konten: BryveKonto[] }> => {
    const { data } = await client.get(`/instances/${id}/bryve/superadmins`, { timeout: 60_000 });
    return data;
  },

  bryveSperren: async (id: string): Promise<{ sperren: BryveSperre[] }> => {
    const { data } = await client.get(`/instances/${id}/bryve/sperren`, { timeout: 60_000 });
    return data;
  },

  bryveSperrenAufheben: async (id: string, ip?: string): Promise<BryveErgebnis> => {
    const { data } = await client.post(
      `/instances/${id}/bryve/sperren-aufheben`, ip ? { ip } : {}, { timeout: 90_000 },
    );
    return data;
  },

  bryvePasswortSetzen: async (
    id: string, email: string, passwort: string,
  ): Promise<BryveErgebnis> => {
    const { data } = await client.post(
      `/instances/${id}/bryve/passwort`, { email, passwort }, { timeout: 90_000 },
    );
    return data;
  },

  // ─── Datenstaende: die Zeitmaschine fuer veraenderliche Dateien ──────

  datenStaende: async (id: string): Promise<{ plaene: DatenStandPlan[] }> => {
    const { data } = await client.get(`/instances/${id}/datenstaende`);
    return data;
  },

  /** Fragt den Wirt nach den Bind-Mounts und misst sie — dauert. */
  datenStandVorschlaege: async (id: string): Promise<{ vorschlaege: PfadVorschlag[] }> => {
    const { data } = await client.get(`/instances/${id}/datenstaende/vorschlaege`, { timeout: 90_000 });
    return data;
  },

  datenStandPlanAnlegen: async (
    id: string,
    body: { pfad: string; intervallMinuten?: number | null; beiDeploy?: boolean; maxStaende?: number },
  ): Promise<DatenStandPlan> => {
    const { data } = await client.post(`/instances/${id}/datenstaende`, body, { timeout: 120_000 });
    return data;
  },

  datenStandPlanEntfernen: async (id: string, planId: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/instances/${id}/datenstaende/${planId}`);
    return data;
  },

  datenStandListe: async (id: string, planId: string): Promise<{ staende: DatenStand[] }> => {
    const { data } = await client.get(`/instances/${id}/datenstaende/${planId}/staende`);
    return data;
  },

  datenStandFesthalten: async (
    id: string, planId: string,
  ): Promise<{ neu: boolean; sha: string | null; meldung: string }> => {
    const { data } = await client.post(
      `/instances/${id}/datenstaende/${planId}/festhalten`, {}, { timeout: 120_000 },
    );
    return data;
  },

  datenStandVergleich: async (id: string, planId: string, sha: string): Promise<{ text: string }> => {
    const { data } = await client.get(
      `/instances/${id}/datenstaende/${planId}/vergleich`, { params: { sha }, timeout: 90_000 },
    );
    return data;
  },

  datenStandZurueckholen: async (
    id: string, planId: string, sha: string,
  ): Promise<{ ok: boolean; meldung: string }> => {
    const { data } = await client.post(
      `/instances/${id}/datenstaende/${planId}/zurueckholen`, { sha }, { timeout: 180_000 },
    );
    return data;
  },

  bryveTfa: async (id: string): Promise<{ konten: BryveTfaKonto[] }> => {
    const { data } = await client.get(`/instances/${id}/bryve/tfa`, { timeout: 60_000 });
    return data;
  },

  bryveTfaZuruecksetzen: async (id: string, email: string): Promise<BryveErgebnis> => {
    const { data } = await client.post(
      `/instances/${id}/bryve/tfa-zuruecksetzen`, { email }, { timeout: 90_000 },
    );
    return data;
  },

  /** Antwortet mit QR-Code und Notfallcodes — einmalig, wie in der Anwendung selbst. */
  bryveTfaEinrichten: async (id: string, email: string): Promise<BryveTfaEinrichtung> => {
    const { data } = await client.post(
      `/instances/${id}/bryve/tfa-einrichten`, { email }, { timeout: 120_000 },
    );
    return data;
  },
};

export interface BryveTfaKonto {
  email: string;
  rolle: string;
  an: boolean;
  hatSecret: boolean;
  /** Ungenutzte Notfallcodes, von zehn. */
  codes: number;
  eingerichtetAm: string | null;
  angelegtAm: string;
  vertrauteGeraete: number;
  /** Die Frist, die in dieser Instanz gilt — nicht die der Vorlage. */
  fristTage: number;
  /** Nicht widerrufene API-Schluessel — ein Dienstkonto meldet sich nie an. */
  apiSchluessel: number;
  /** SUPERADMIN ohne zweiten Faktor, aelter als die Frist — kommt nicht mehr hinein. */
  ausgesperrt: boolean;
}

export interface BryveTfaEinrichtung {
  ok: boolean;
  meldung: string;
  email: string;
  qr: string | null;
  secret: string | null;
  codes: string[];
}

export interface DatenStandPlan {
  id: string;
  pfad: string;
  aktiv: boolean;
  /** null = nur beim Ausrollen und von Hand. */
  intervallMinuten: number | null;
  beiDeploy: boolean;
  maxStaende: number;
  letzterLauf: string | null;
  letzterFehler: string | null;
  anzahl: number;
}

export interface PfadVorschlag {
  pfad: string;
  bytes: number;
  dateien: number;
  /** Leer, wenn der Pfad aufgenommen werden kann. Sonst der Grund dagegen. */
  hindernis: string | null;
}

export interface DatenStand {
  id: string;
  sha: string;
  /** 'plan' | 'deploy' | 'hand' */
  anlass: string;
  dateien: number;
  bytes: string;
  aenderung: string | null;
  ausgeloestVon: string | null;
  createdAt: string;
}

export interface BridgeBefund {
  linkId: string;
  wirt: string;
  gast: string;
  befunde: {
    schritt: string;
    ampel: 'gruen' | 'gelb' | 'rot';
    ergebnis: string;
    deutung: string | null;
    rat: string | null;
  }[];
  /** Der erste rote Schritt. */
  bruchstelle: string | null;
  /** Nur wahr, wenn die Anfrage am Ende wirklich durchging. */
  steht: boolean;
  zusammenfassung: string;
  geprueftAm: string;
}

export interface BryveKonto {
  email: string;
  name: string | null;
  rolle: string;
}

export interface BryveSperre {
  ip: string;
  grund: string;
  gesperrtAm: string;
  laeuftAbAm: string;
  treffer: number;
}

export interface BryveErgebnis {
  ok: boolean;
  meldung: string;
  /** Was versucht wurde — 'schnittstelle' zuerst, 'datenbank' als Rueckfall. */
  wege: { name: string; ok: boolean; meldung: string }[];
}

export interface InstanzContainerListe {
  instanceId: string;
  server: string | null;
  container: { name: string; status: string; image: string; projekt: string | null }[];
  /** Warum die Liste leer ist, wenn sie leer ist. */
  hinweis: string | null;
}

export interface ContainerProtokoll {
  container: string;
  zeilen: number;
  text: string;
  /** Am Anfang gekappt — dann fehlt oben etwas, und mehr Zeilen helfen. */
  abgeschnitten: boolean;
  server: string | null;
}

export type TlsAmpel = 'gruen' | 'gelb' | 'rot' | 'unbekannt';

export interface TlsBefund {
  schritt: string;
  ampel: TlsAmpel;
  /** Was gemessen wurde. */
  ergebnis: string;
  /** Was das bedeutet — nur gefuellt, wenn etwas nicht stimmt. */
  deutung: string | null;
  /** Was zu tun waere. */
  rat: string | null;
}

export interface TlsDiagnose {
  instanceId: string;
  hostname: string | null;
  server: string | null;
  befunde: TlsBefund[];
  /** Der erste Schritt, der nicht gruen ist — dort reisst die Kette. */
  bruchstelle: string | null;
  zusammenfassung: string;
  geprueftAm: string;
}

export interface StackKandidat {
  projekt: string;
  container: number;
  laufend: number;
  erfasst: boolean;
  vermutetesProdukt: string | null;
}

export interface TlsSicherstellenErgebnis {
  ok: boolean;
  ergebnisse: { adresse: string; ok: boolean; meldung: string; zertifikatId?: number }[];
}

// ─── Orders ───────────────────────────────────────────────────────────────────

export interface OrderListParams {
  status?: string;
  type?: string;
  page?: number;
  pageSize?: number;
}

export const orders = {
  list: async (params?: OrderListParams): Promise<Order[]> => {
    return alleSeiten<Order>('/orders', params as Record<string, unknown> | undefined);
  },
  get: async (id: string): Promise<Order> => {
    const { data } = await client.get(`/orders/${id}`);
    return data;
  },
  create: async (payload: Partial<Order>): Promise<Order> => {
    const { data } = await client.post('/orders', payload);
    return data;
  },
  updateStatus: async (id: string, status: OrderStatus): Promise<Order> => {
    const { data } = await client.patch(`/orders/${id}/status`, { status });
    return data;
  },
  provision: async (id: string): Promise<DeploymentJob> => {
    const { data } = await client.post(`/orders/${id}/provision`);
    return data;
  },
};

// ─── Customers ────────────────────────────────────────────────────────────────

export interface CustomerListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export const customers = {
  list: async (params?: CustomerListParams): Promise<Customer[]> => {
    return alleSeiten<Customer>('/customers', params as Record<string, unknown> | undefined);
  },
  get: async (id: string): Promise<Customer> => {
    const { data } = await client.get(`/customers/${id}`);
    return data;
  },
  create: async (payload: Partial<Customer>): Promise<Customer> => {
    const { data } = await client.post('/customers', payload);
    return data;
  },
  update: async (id: string, payload: Partial<Customer>): Promise<Customer> => {
    const { data } = await client.patch(`/customers/${id}`, payload);
    return data;
  },
  getOrders: async (id: string): Promise<Order[]> => {
    const { data } = await client.get(`/customers/${id}/orders`);
    return data;
  },
  getInstances: async (id: string): Promise<Instance[]> => {
    const { data } = await client.get(`/customers/${id}/instances`);
    return data;
  },
  suspend: async (id: string): Promise<Customer> => {
    const { data } = await client.post(`/customers/${id}/suspend`);
    return data;
  },
  reactivate: async (id: string): Promise<Customer> => {
    const { data } = await client.post(`/customers/${id}/reactivate`);
    return data;
  },
  getUsers: async (id: string): Promise<CustomerUser[]> => {
    const { data } = await client.get(`/customers/${id}/users`);
    return data;
  },
  inviteUser: async (
    id: string,
    payload: { email: string; name: string; role: CustomerRole },
  ): Promise<CustomerUser> => {
    const { data } = await client.post(`/customers/${id}/users`, payload);
    return data;
  },
  updateUser: async (
    id: string,
    userId: string,
    payload: { name?: string; role?: CustomerRole; status?: 'active' | 'inactive' },
  ): Promise<CustomerUser> => {
    const { data } = await client.put(`/customers/${id}/users/${userId}`, payload);
    return data;
  },
  sendUserPasswordReset: async (id: string, userId: string): Promise<{ message: string }> => {
    const { data } = await client.post(`/customers/${id}/users/${userId}/send-password-reset`);
    return data;
  },

  /** Hinterlegte Anbieter-Zugänge. Das Token kommt nie zurück. */
  providerCredentials: async (
    id: string,
  ): Promise<{ credentials: ProviderCredential[]; anbieter: string[] }> => {
    const { data } = await client.get(`/customers/${id}/provider-credentials`);
    return data;
  },

  /**
   * Legt einen Zugang an oder ersetzt ihn.
   *
   * Das Backend prüft das Token gleich mit und speichert auch dann, wenn die
   * Prüfung scheitert — der häufigste Fall ist ein Tippfehler beim Einfügen,
   * und der soll sichtbar werden statt das Speichern zu verhindern.
   */
  setProviderCredential: async (
    id: string,
    provider: string,
    token: string,
    label?: string,
  ): Promise<{ credential: ProviderCredential; check: ProviderCheck; message: string }> => {
    const { data } = await client.put(`/customers/${id}/provider-credentials/${provider}`, {
      token,
      label,
    });
    return data;
  },

  checkProviderCredential: async (id: string, provider: string): Promise<ProviderCheck> => {
    const { data } = await client.post(`/customers/${id}/provider-credentials/${provider}/check`);
    return data;
  },

  deleteProviderCredential: async (id: string, provider: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/customers/${id}/provider-credentials/${provider}`);
    return data;
  },

  /**
   * Paketserver-Zugänge aus Lizenzen, die der Kunde gekauft hat.
   *
   * Nicht zu verwechseln mit den Anbieter-Zugängen darüber: die öffnen einen
   * Server-Anbieter, diese einen Composer-Paketserver. Hyvä ist der erste
   * Fall — die Lizenz gilt je Shop, also wird das Magento-Image des Kunden
   * mit seinem Zugang gebaut.
   *
   * Das Passwort kommt nie zurück.
   */
  lizenzen: async (id: string): Promise<KundenLizenz[]> => {
    const { data } = await client.get(`/customers/${id}/lizenzen`);
    return data;
  },

  lizenzAnlegen: async (id: string, eingabe: LizenzEingabe): Promise<KundenLizenz> => {
    const { data } = await client.post(`/customers/${id}/lizenzen`, eingabe);
    return data;
  },

  lizenzAendern: async (
    id: string,
    lizenzId: string,
    eingabe: Partial<LizenzEingabe>,
  ): Promise<KundenLizenz> => {
    const { data } = await client.put(`/customers/${id}/lizenzen/${lizenzId}`, eingabe);
    return data;
  },

  lizenzLoeschen: async (id: string, lizenzId: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/customers/${id}/lizenzen/${lizenzId}`);
    return data;
  },
};

export interface KundenLizenz {
  id: string;
  paketServer: string;
  bezeichnung: string;
  benutzer: string;
  notiz: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LizenzEingabe {
  paketServer: string;
  bezeichnung: string;
  benutzer: string;
  passwort: string;
  notiz?: string | null;
}

// ─── Deployments ──────────────────────────────────────────────────────────────

export interface DeploymentListParams {
  status?: string;
  type?: string;
  page?: number;
  pageSize?: number;
}

export const deployments = {
  list: async (params?: DeploymentListParams): Promise<DeploymentJob[]> => {
    return alleSeiten<DeploymentJob>('/deployments', params as Record<string, unknown> | undefined);
  },
  get: async (id: string): Promise<DeploymentJob> => {
    const { data } = await client.get(`/deployments/${id}`);
    return data;
  },
  retry: async (id: string): Promise<DeploymentJob> => {
    const { data } = await client.post(`/deployments/${id}/retry`);
    return data;
  },
  cancel: async (id: string): Promise<DeploymentJob> => {
    const { data } = await client.post(`/deployments/${id}/cancel`);
    return data;
  },
};

// ─── Releases ─────────────────────────────────────────────────────────────────

export interface ReleaseListParams {
  productId?: string;
  status?: string;
  channel?: string;
  page?: number;
  pageSize?: number;
}

export const releases = {
  list: async (params?: ReleaseListParams): Promise<Release[]> => {
    return alleSeiten<Release>('/releases', params as Record<string, unknown> | undefined);
  },
  get: async (id: string): Promise<Release> => {
    const { data } = await client.get(`/releases/${id}`);
    return data;
  },
  create: async (payload: Partial<Release>): Promise<Release> => {
    const { data } = await client.post('/releases', payload);
    return data;
  },
  update: async (id: string, payload: Partial<Release>): Promise<Release> => {
    const { data } = await client.patch(`/releases/${id}`, payload);
    return data;
  },
  approve: async (id: string): Promise<Release> => {
    const { data } = await client.post(`/releases/${id}/approve`);
    return data;
  },
  rollout: async (id: string): Promise<Release> => {
    const { data } = await client.post(`/releases/${id}/rollout`);
    return data;
  },
};

// ─── Audit ────────────────────────────────────────────────────────────────────

export interface AuditListParams {
  action?: string;
  entityType?: string;
  /** Auf ein einzelnes Objekt einschränken — etwa eine Instanz. */
  entityId?: string;
  userId?: string;
  /** ISO-Datum. */
  from?: string;
  to?: string;
  page?: number;
  /** So heisst der Parameter im Backend. `pageSize` wurde dort nie gelesen. */
  limit?: number;
}

export const audit = {
  list: async (params?: AuditListParams): Promise<PaginatedResponse<AuditLog>> => {
    const { data } = await client.get('/audit', { params });
    return data;
  },

  /**
   * Das Protokoll als CSV, mit denselben Filtern wie die Liste.
   *
   * Wir sagen Kunden zu, dass unsere Zugriffe auf ihre Instanz nachweisbar
   * sind. Ein Nachweis, den man nur ansehen und nicht verschicken kann, ist
   * keiner.
   */
  exportCsv: async (params?: AuditListParams): Promise<Blob> => {
    const { data } = await client.get('/audit/export.csv', { params, responseType: 'blob' });
    return data;
  },
};

// ─── Integrations (provider API tokens) ────────────────────────────────────────

/** Ein Eintrag der Verwaltungsliste. `ablauf: null` heisst dauerhaft. */
export interface AdressEintrag {
  adresse: string;
  ablauf: string | null;
  abgelaufen: boolean;
}

export interface IntegrationToken {
  /**
   * Keine Geheimniszeile, sondern eine Einstellung — der Wert wird
   * mitgeliefert und darf im Formular vorbelegt werden.
   */
  offen?: boolean;
  /** Nur bei `offen` gesetzt: der aktuelle Wert, zum Ergänzen statt Neutippen. */
  wert?: string;
  /** Nur bei der Verwaltungsliste: der Wert aufgeschlüsselt. */
  adressen?: AdressEintrag[];
  key: string;
  label: string;
  description: string;
  /** Heading to file this under. */
  group: string;
  /**
   * Braucht ein mehrzeiliges Feld statt einer Zeile.
   *
   * Fuer die JSON-Datei eines Google-Dienstkontos. In ein einzeiliges Feld
   * geklebt verliert regelmaessig jemand die Haelfte, und es faellt erst beim
   * ersten Abruf auf.
   */
  mehrzeilig?: boolean;
  /**
   * Beschriftung des Knopfes statt „Ersetzen".
   *
   * Fuer die Verwaltungsadressen: dort wird ergaenzt und gestrichen, nicht
   * ersetzt — und wer „Ersetzen" liest, tippt im schlimmsten Fall nur seine
   * eigene Adresse ein.
   */
  aktionLabel?: string;
  hasValue: boolean;
  /**
   * 'database' — set here and removable. 'environment' — came with the
   * deployment; still working, but not editable from here until it is set.
   */
  source: 'database' | 'environment' | null;
  updatedAt: string | null;
}

export const integrations = {
  /**
   * Die Eintraege samt der Adresse, unter der der Aufrufer hier ankommt.
   *
   * Die Antwort war frueher ein blankes Array. Aeltere Faelle, die noch eines
   * erwarten, werden hier aufgefangen -- ein Formularfeld soll nicht leer
   * bleiben, weil sich die Antwortform geaendert hat.
   */
  list: async (): Promise<{ eintraege: IntegrationToken[]; deineAdresse: string | null }> => {
    const { data } = await client.get('/integrations');
    return Array.isArray(data) ? { eintraege: data, deineAdresse: null } : data;
  },
  set: async (key: string, value: string): Promise<IntegrationToken> => {
    const { data } = await client.put(`/integrations/${key}`, { value });
    return data;
  },
  remove: async (key: string): Promise<void> => {
    await client.delete(`/integrations/${key}`);
  },
};

// ─── API Keys (CLI / MCP / agent access) ───────────────────────────────────────

export interface ApiKeyMeta {
  id: string;
  name: string;
  role: string;
  keyPrefix: string;
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface ApiKeyCreated extends Omit<ApiKeyMeta, 'createdBy' | 'revokedAt' | 'lastUsedAt'> {
  rawKey: string;
}

export const apiKeys = {
  list: async (): Promise<ApiKeyMeta[]> => {
    const { data } = await client.get('/api-keys');
    return data;
  },
  create: async (name: string, role: string): Promise<ApiKeyCreated> => {
    const { data } = await client.post('/api-keys', { name, role });
    return data;
  },
  revoke: async (id: string): Promise<void> => {
    await client.delete(`/api-keys/${id}`);
  },
};

export default client;

// ─── Embed keys (pricing widgets on marketing sites) ──────────────────────────

export const embedKeys = {
  list: async (): Promise<EmbedKey[]> => {
    const { data } = await client.get('/embed-keys');
    return data;
  },
  create: async (payload: {
    name: string;
    allowedOrigins: string[];
    productIds?: string[];
  }): Promise<EmbedKey> => {
    const { data } = await client.post('/embed-keys', payload);
    return data;
  },
  update: async (id: string, payload: Partial<{ name: string; allowedOrigins: string[] }>): Promise<EmbedKey> => {
    const { data } = await client.put(`/embed-keys/${id}`, payload);
    return data;
  },
  revoke: async (id: string): Promise<void> => {
    await client.delete(`/embed-keys/${id}`);
  },
  signups: async (id: string): Promise<PendingSignup[]> => {
    const { data } = await client.get(`/embed-keys/${id}/signups`);
    return data;
  },
};

// ─── Activation (self-hosted instances) ────────────────────────────────────────

export interface SetActivationStatePayload {
  state: ActivationState;
  reason: string;
  /** Required by the API when moving to SUSPENDED or TERMINATED. */
  notice?: {
    title: string;
    body: string;
    audience?: NoticeAudience;
    dismissible?: boolean;
  };
}

export interface NoticePayload {
  severity?: NoticeSeverity;
  audience?: NoticeAudience;
  title: string;
  body: string;
  dismissible?: boolean;
  activeUntil?: string | null;
}

export const activation = {
  listInstances: async (): Promise<SelfHostedInstance[]> => {
    const { data } = await client.get('/activation/instances');
    return data.data ?? data;
  },
  setState: async (
    instanceId: string,
    payload: SetActivationStatePayload,
  ): Promise<{ activationState: ActivationState; activationReason: string | null }> => {
    const { data } = await client.patch(`/activation/instances/${instanceId}/state`, payload);
    return data;
  },
  heartbeats: async (instanceId: string): Promise<InstanceHeartbeat[]> => {
    const { data } = await client.get(`/activation/instances/${instanceId}/heartbeats`);
    return data.data ?? data;
  },
  // Returns the key exactly once; it is not readable afterwards.
  issueKey: async (
    instanceId: string,
  ): Promise<{ activationKey: string; reissued: boolean; note: string }> => {
    const { data } = await client.post(`/activation/instances/${instanceId}/key`);
    return data;
  },
  rotateToken: async (
    instanceId: string,
  ): Promise<{ agentToken: string; note: string }> => {
    const { data } = await client.post(`/activation/instances/${instanceId}/rotate-token`);
    return data;
  },
  listNotices: async (instanceId: string): Promise<InstanceNotice[]> => {
    const { data } = await client.get(`/activation/instances/${instanceId}/notices`);
    return data.data ?? data;
  },
  createNotice: async (instanceId: string, payload: NoticePayload): Promise<InstanceNotice> => {
    const { data } = await client.post(`/activation/instances/${instanceId}/notices`, payload);
    return data;
  },
  retireNotice: async (noticeId: string): Promise<void> => {
    await client.delete(`/activation/notices/${noticeId}`);
  },
};

// ─── Container registry (read-only, for the release form) ──────────────────────

export interface RegistryStatus {
  configured: boolean;
  url?: string;
  reachable?: boolean;
  message?: string;
}

export interface RegistryTag {
  tag: string;
  digest?: string;
  createdAt?: string;
  sizeBytes?: number;
}

export const registry = {
  status: async (): Promise<RegistryStatus> => {
    const { data } = await client.get('/registry/status');
    return data;
  },
  repositories: async (): Promise<{ host: string | null; repositories: string[] }> => {
    const { data } = await client.get('/registry/repositories');
    return data;
  },
  tags: async (
    repository: string,
    details = false,
  ): Promise<{ host: string | null; repository: string; tags: RegistryTag[] }> => {
    const { data } = await client.get('/registry/tags', { params: { repository, details } });
    return data;
  },
  refresh: async (): Promise<void> => {
    await client.post('/registry/refresh');
  },
};

export type CheckStatus = 'PASS' | 'WARN' | 'FAIL' | 'UNKNOWN';

export type FixAction =
  | 'registry-account'
  | 'registry-login'
  | 'registry-revoke'
  | 'compose-plugin'
  | 'opt-juni-dir'
  | 'host-baseline'
  | 'firewall-refresh';

/** Present when the PC can repair the condition over SSH itself. */
export interface HostCheckFix {
  action: FixAction;
  label: string;
  /** Exactly what will run — show this before running it. */
  description: string;
  /** Additional warning for hosts we do not own. */
  caution?: string;
  /** 'revoke' takes access away and hangs off a passing check — must look different. */
  kind?: 'repair' | 'revoke';
}

export interface HostCheck {
  key: string;
  group: 'Zugang' | 'Docker' | 'Grundausstattung' | 'Ressourcen' | 'Netz' | 'Zeit' | 'Ablage';
  label: string;
  status: CheckStatus;
  value: string | null;
  detail: string | null;
  /** A failure here means nothing can be deployed to this host. */
  blocking: boolean;
  fix?: HostCheckFix;
}

export interface HostCheckResult {
  serverId: string;
  hostname: string;
  ownership: string;
  ok: boolean;
  checks: HostCheck[];
  summary: { pass: number; warn: number; fail: number; unknown: number };
  warnings: string[];
  durationMs: number;
  collectedAt: string;
}

export interface FixResult {
  ok: boolean;
  log: string;
  error?: string;
}

export interface RegistryAccount {
  username: string;
  createdAt: string;
  rotatedAt: string | null;
}

export interface MyRegistryCredential {
  registry: string | null;
  /** False when the PC cannot manage credentials — the reason is in `missing`. */
  available: boolean;
  missing: string[];
  credential: RegistryAccount | null;
}

/** The one and only time the password is returned. */
export interface IssuedRegistryCredential {
  username: string;
  password: string;
  rotated: boolean;
  registry: string | null;
  loginCommand: string;
}

export interface RegistryCredentialRow {
  id: string;
  username: string;
  createdAt: string;
  rotatedAt: string | null;
  kind: 'user' | 'server';
  user: { id: string; name: string; email: string; status: string } | null;
  server: { id: string; hostname: string; ownership: string } | null;
}

export const registryCredentials = {
  mine: async (): Promise<MyRegistryCredential> => {
    const { data } = await client.get('/registry-credentials/me');
    return data;
  },
  /** Creates one, or rotates the existing one — same call by design. */
  issueMine: async (): Promise<IssuedRegistryCredential> => {
    const { data } = await client.post('/registry-credentials/me');
    return data;
  },
  revokeMine: async (): Promise<{ username: string }> => {
    const { data } = await client.delete('/registry-credentials/me');
    return data;
  },
  list: async (): Promise<RegistryCredentialRow[]> => {
    const { data } = await client.get('/registry-credentials');
    return data.data ?? data;
  },
  revoke: async (id: string): Promise<{ username: string }> => {
    const { data } = await client.delete(`/registry-credentials/${id}`);
    return data;
  },
};

/**
 * Woher der Wert einer Stack-Variable kommt.
 *
 * `BERECHNET` ist der Unterschied zu `AUTO`, auf den es ankommt: AUTO leitet
 * einmal ab und behaelt den Wert, BERECHNET ermittelt ihn bei jedem Ausrollen
 * neu. Fuer die Tunnel-Weiterleitungen ist das der ganze Sinn — eine neue
 * Instanz ist dadurch von selbst dabei.
 */
export type VariableSource = 'AUTO' | 'GENERATE' | 'GLOBAL' | 'PER_SERVER' | 'BERECHNET';

export interface StackVariable {
  key: string;
  label?: string;
  description?: string;
  source: VariableSource;
  autoField?: 'hostname' | 'hostnameShort' | 'hostnameSlug' | 'ipAddress' | 'id' | 'region';
  generateBytes?: number;
  /** '__UNCHANGED__' means a secret is stored and was not returned. */
  value?: string;
  default?: string;
  secret?: boolean;
}

export interface BaseStack {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  version: string;
  status: 'DRAFT' | 'ACTIVE' | 'DEPRECATED';
  composeContent: string;
  envTemplate: string | null;
  variables: StackVariable[];
  requiredNetworks: string[];
  requiredPaths: string[];
  declaredPorts: number[];
  isDefault: boolean;
  deployments?: { serverId: string; deployedVersion: string | null }[];
}

export interface PreflightIssue {
  level: 'error' | 'warning';
  message: string;
}

export interface PreflightResult {
  ok: boolean;
  issues: PreflightIssue[];
  occupiedPorts: number[];
  missingNetworks: string[];
}

/** One variable as it applies to one server: what it would become, and whether it was pinned. */
export interface ServerStackVariable {
  key: string;
  label?: string;
  description?: string;
  source: VariableSource;
  secret: boolean;
  /** The value this host would get without intervention. Null for secrets. */
  suggestion: string | null;
  /** A value was stored for this host — the suggestion is not what is in use. */
  overridden: boolean;
  /** False means a deploy would abort on this variable: it needs a value typed in. */
  resolvable: boolean;
}

export interface ServerBaseStack {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  version: string;
  status: BaseStack['status'];
  isDefault: boolean;
  declaredPorts: number[];
  variables: ServerStackVariable[];
  deployment: {
    status: 'PENDING' | 'DEPLOYING' | 'ACTIVE' | 'FAILED' | 'REMOVED';
    deployedVersion: string | null;
    lastDeployedAt: string | null;
    lastError: string | null;
    lastLog: string | null;
    /** Definition changed since this host was deployed. */
    outdated: boolean;
  } | null;
}

export interface DeployResult {
  ok: boolean;
  log: string;
  error?: string;
  preflight?: PreflightResult;
}

export const baseStacks = {
  list: async (): Promise<BaseStack[]> => {
    const { data } = await client.get('/base-stacks');
    return data.data ?? data;
  },
  get: async (id: string): Promise<BaseStack> => {
    const { data } = await client.get(`/base-stacks/${id}`);
    return data;
  },
  create: async (payload: Partial<BaseStack>): Promise<BaseStack> => {
    const { data } = await client.post('/base-stacks', payload);
    return data;
  },
  update: async (id: string, payload: Partial<BaseStack>): Promise<BaseStack> => {
    const { data } = await client.patch(`/base-stacks/${id}`, payload);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await client.delete(`/base-stacks/${id}`);
  },
  /** Reads placeholders, host ports and external networks out of a compose file. */
  analyse: async (
    composeContent: string,
  ): Promise<{ placeholders: string[]; ports: number[]; networks: string[] }> => {
    const { data } = await client.post('/base-stacks/analyse', { composeContent });
    return data;
  },
  forServer: async (serverId: string): Promise<ServerBaseStack[]> => {
    const { data } = await client.get(`/base-stacks/servers/${serverId}`);
    return data.data ?? data;
  },
  preflight: async (id: string, serverId: string): Promise<PreflightResult> => {
    const { data } = await client.get(`/base-stacks/${id}/preflight/${serverId}`);
    return data;
  },
  deploy: async (
    id: string,
    serverId: string,
    values: Record<string, string> = {},
    force = false,
  ): Promise<DeployResult> => {
    const { data } = await client.post(`/base-stacks/${id}/deploy/${serverId}`, { values, force });
    return data;
  },
  undeploy: async (id: string, serverId: string): Promise<DeployResult> => {
    const { data } = await client.delete(`/base-stacks/${id}/deploy/${serverId}`);
    return data;
  },
};

// ─── Domainübersicht ──────────────────────────────────────────────────────────
//
// Der Name der Seite lautet "Domainverwaltung", und das ist eine Untertreibung
// in die falsche Richtung: hier wird nichts verwaltet. Registriert, verlängert
// und gekündigt wird beim Registrar. Diese Aufrufe beantworten Fragen.

export type DomainStatus = 'ACTIVE' | 'PARKED' | 'REDIRECT' | 'EXPIRING' | 'TRANSFERRED' | 'UNKNOWN';
export type MonitorInterval = 'OFF' | 'HOURLY' | 'TWELVE_HOURS' | 'DAILY';
/**
 * Muss vollständig zu `enum DomainCheckResult` in schema.prisma passen.
 *
 * `ORIGIN_DOWN` und `BLOCKED` fehlten hier, nachdem sie im Backend dazukamen —
 * und der Typ hat den Fehler nicht nur verdeckt, sondern **abgesegnet**: ein
 * `Record<DomainCheckResult, …>` mit acht Einträgen war damit vollständig,
 * TypeScript zufrieden, und die Anzeige stürzte ab, sobald eine Domain mit
 * einem der beiden Werte in die Liste kam. Auf Seite 1 stand keine — deshalb
 * fiel es erst beim Suchen auf, was wie ein Fehler der Suche aussah.
 *
 * Dieselbe Klasse Fehler wie bei `PaginatedResponse`, siehe LES-2026-007.
 */
export type DomainCheckResult =
  | 'OK' | 'PLACEHOLDER' | 'REDIRECT' | 'HTTP_ERROR'
  | 'ORIGIN_DOWN' | 'BLOCKED'
  | 'TLS_ERROR' | 'TIMEOUT' | 'DNS_ERROR' | 'CONN_ERROR';

export interface HostingProvider {
  id: string;
  name: string;
  slug: string;
  website?: string | null;
  /** Wo man sich anmeldet — die Startseite eines Registrars hilft niemandem. */
  loginUrl?: string | null;
  notes?: string | null;
  isBuiltIn: boolean;
  _count?: { domains: number };
}

export interface DnsRecord {
  id: string;
  type: string;
  name: string;
  value: string;
  ttl?: number | null;
  priority?: number | null;
  checkedAt: string;
}

export interface Subdomain {
  id: string;
  domainId: string;
  label: string;
  fqdn: string;
  source: 'MANUAL' | 'IMPORT' | 'CT';
  resolvedIp?: string | null;
  resolvedCname?: string | null;
  lastDnsCheckAt?: string | null;
  monitorInterval: MonitorInterval;
  lastCheckAt?: string | null;
  lastResult?: DomainCheckResult | null;
  notes?: string | null;
  /**
   * Wird diese Subdomain bei der Adresserhebung mitgefragt?
   *
   * Noetig fuer Quellen ohne Domain-Eigenschaft — Bing fuehrt jede Subdomain
   * als eigene Site. Die Search Console deckt mit `sc-domain:` ohnehin alles
   * ab und braucht das nicht.
   */
  inQuellen?: boolean;
}

export interface DomainCheck {
  id: string;
  url: string;
  result: DomainCheckResult;
  statusCode?: number | null;
  durationMs?: number | null;
  redirectTo?: string | null;
  /** Welche Standardseite erkannt wurde — "nginx-default", "npm-default", … */
  placeholderKind?: string | null;
  tlsDaysLeft?: number | null;
  tlsIssuer?: string | null;
  error?: string | null;
  checkedAt: string;
}

/**
 * Der Eigentumsstatus — eine Entscheidung, keine Beobachtung. `status` daneben
 * sagt, was ist. `PARKED` gibt es in beiden: dort „zeigt nirgendwohin", hier
 * „soll nirgendwohin zeigen". Der Import fasst dieses Feld nie an.
 */
export type DomainOwnership =
  | 'UNDECIDED' | 'RENEW' | 'HOLD' | 'PARKED' | 'REVIEW' | 'TRANSFER' | 'DROP';

// ─── KI-Gateway (ai-loadbalancer) ───────────────────────────────────────────
//
// Alles laeuft ueber das PC-Backend, nicht direkt gegen llama-api.juni.com:
// der Verwaltungsschluessel darf nicht in ein Buendel, das jeder Browser
// herunterlaedt. Nebenbei entfaellt CORS, und die Rollen des PC greifen.

export interface GatewayNode {
  id: number;
  name: string;
  base_url: string;
  enabled: boolean;
  draining: boolean;
  status: 'UNKNOWN' | 'ONLINE' | 'DEGRADED' | 'OFFLINE';
  ollama_version?: string | null;
  last_seen_at?: string | null;
  last_error?: string | null;
  active_requests: number;
  group_name?: string | null;
  models: Array<{
    name: string;
    size_bytes?: number | null;
    loaded?: boolean;
    capabilities?: string[];
  }>;
}

export interface GatewayStatus {
  /** Sind Adresse und Schluessel hinterlegt? `false` ist kein Fehler. */
  eingerichtet: boolean;
  hinweis?: string;
  erreichbar?: boolean;
  version?: string | null;
  fehler?: string | null;
  kennzahlen?: {
    knoten: number;
    online: number;
    auffaellig: number;
    abgeschaltet: number;
    wirdGeleert: number;
    laufendeAnfragen: number;
    modelle: number;
  };
  nodes?: GatewayNode[];
  adressen?: { api: string | null; oberflaeche: string | null };
}

export const inference = {
  status: async (): Promise<GatewayStatus> => {
    const { data } = await client.get('/inference/status');
    return data;
  },
  keys: async (): Promise<unknown[]> => {
    const { data } = await client.get('/inference/keys');
    return data;
  },
};

// ─── Verlagsverbund (Control Plane) ───────────────────────────────────────────
//
// Alles läuft über das Backend (`/api/verbund`); das Verwaltungs-Token der CP
// kommt nie in den Browser. Was die angemeldete Person darf, liefert `status`
// in `rechte` — die Tabelle steht nur im Backend (lib/verbund-rechte.ts).

export type VerbundAktion =
  | 'lesen' | 'antrag_entscheiden' | 'neues_token' | 'abgleich'
  | 'sperren' | 'sperre_aufheben' | 'ausschliessen' | 'ausschluss_aufheben';

export interface VerbundStatus {
  netzName: string;
  eingerichtet: boolean;
  erreichbar: boolean;
  fehler: string | null;
  cpUrl: string | null;
  seiten: Record<'start' | 'registrierung' | 'teilnahmebedingungen' | 'anbindung' | 'spec', string> | null;
  rechte: Record<VerbundAktion, boolean>;
  offeneSperren?: number | null;
}

export type AntragStatus = 'eingereicht' | 'url_geprueft' | 'freigegeben' | 'abgelehnt' | 'angemeldet';

export interface VerbundAntrag {
  id: string;
  status: AntragStatus;
  verlag: string;
  land: string;
  website: string;
  ansprechperson: string;
  email: string;
  baseUrl: string;
  beschreibung: string;
  bedingungenVersion: string;
  urlGeprueftAt: string | null;
  letztePruefungFehler: string | null;
  publisherSlug: string | null;
  residency: string | null;
  participates: boolean;
  entschiedenAt: string | null;
  entschiedenVon: string | null;
  ablehnungsgrund: string | null;
  notiz: string | null;
  tokenAbholbarBis: string | null;
  tokenAbgeholtAt: string | null;
  nodeId: string | null;
  angemeldetAt: string | null;
  konformitaet: unknown;
  createdAt: string;
  tokens?: Array<{ id: string; createdAt: string; expiresAt: string; usedAt: string | null; widerrufenAt: string | null; nodeId: string | null }>;
}

export type VerlagStatus = 'aktiv' | 'gesperrt' | 'ausgeschlossen';

export interface VerbundKnoten {
  id: string;
  baseUrl: string;
  status: string;
  participates: boolean;
  residency: string | null;
  software: string | null;
  letzterAbgleich: string | null;
  abgleichFehler: string | null;
  fehlerKlasse: string | null;
  eintraege: number | null;
  verlag: { id: string; slug: string; name: string; status: VerlagStatus } | null;
  instanz: { id: string; name: string } | null;
}

export interface VerbundVerlag {
  id: string;
  slug: string;
  name: string;
  status: VerlagStatus;
  createdAt: string | null;
  knotenAnzahl: number;
  katalogEintraege: number;
  /** Über die Selbst-Registrierung angemeldet; `null`, wenn die CP es nicht sagt. */
  registriert: boolean | null;
  knoten: VerbundKnoten[];
}

/** Ein Verlag im Detail — die Knoten tragen hier den Rohtext des Abgleichfehlers (`abgleichFehler`). */
export interface VerbundVerlagDetail extends VerbundVerlag {
  registrierungen: Array<{ id: string; status: AntragStatus; baseUrl: string; nodeId: string | null; createdAt: string; angemeldetAt: string | null }>;
  sperren: {
    anzahl: number;
    offen: { id: string; art: 'gesperrt' | 'ausgeschlossen'; grund: string; von: string; createdAt: string } | null;
    letzte: { id: string; art: 'gesperrt' | 'ausgeschlossen'; createdAt: string; aufgehobenAt: string | null } | null;
  } | null;
}

/** Eine Seite einer serverseitig geblätterten Liste des Verbunds. */
export interface VerbundSeite<T> {
  eintraege: T[];
  gesamt: number;
  seite: number;
  proSeite: number;
}

export type VerbundKnotenFilter = 'ACTIVE' | 'SUSPENDED' | 'EXCLUDED' | 'fehler' | 'ohne';
export type VerbundSortierung = 'name' | 'name_absteigend' | 'slug' | 'neueste' | 'aelteste';
export interface VerbundVerlagFilter {
  seite?: number;
  proSeite?: number;
  q?: string;
  status?: VerlagStatus;
  knotenStatus?: VerbundKnotenFilter;
  sortierung?: VerbundSortierung;
}
export interface VerbundVereinbarungFilter {
  seite?: number;
  proSeite?: number;
  verlag?: string;
  status?: string;
}

export interface VerbundSperre {
  id: string;
  publisherId: string;
  art: 'gesperrt' | 'ausgeschlossen';
  grund: string;
  von: string;
  domains: string[];
  nodeIds: string[];
  agreementIds: string[];
  tokenIds: string[];
  createdAt: string;
  aufgehobenAt: string | null;
  aufgehobenVon: string | null;
  aufhebungsGrund: string | null;
  verlag?: { id: string; slug: string; name: string; status: VerlagStatus } | null;
}

export interface VerbundVereinbarung {
  id: string;
  agreementKey: string;
  version: number;
  /** Anzahl aller Versionen des Paares; die Liste zeigt nur die aktuelle. */
  versionen: number;
  status: string;
  nettingMode: string | null;
  validFrom: string | null;
  validUntil: string | null;
  verlagA: { slug: string; name: string; status: string };
  verlagB: { slug: string; name: string; status: string };
  aToB: { visible?: boolean; referralPct?: number } | null;
  bToA: { visible?: boolean; referralPct?: number } | null;
}

export interface VerbundInstanzKnoten {
  elibrary: boolean;
  netzName?: string;
  eingerichtet?: boolean;
  knoten?: VerbundKnoten[];
  fehler?: string;
}

export const verbund = {
  status: async (): Promise<VerbundStatus> => (await client.get('/verbund/status')).data,
  antraege: async (status?: AntragStatus): Promise<VerbundAntrag[]> =>
    (await client.get('/verbund/antraege', { params: status ? { status } : {} })).data,
  antrag: async (id: string): Promise<VerbundAntrag> => (await client.get(`/verbund/antraege/${id}`)).data,
  freigeben: async (id: string, b: { publisherSlug?: string; bestehenderVerlag?: boolean; residency?: string; participates?: boolean; notiz?: string }) =>
    (await client.post(`/verbund/antraege/${id}/freigeben`, b)).data,
  ablehnen: async (id: string, grund?: string) =>
    (await client.post(`/verbund/antraege/${id}/ablehnen`, grund ? { grund } : {})).data,
  neuesToken: async (id: string) => (await client.post(`/verbund/antraege/${id}/neues-token`, {})).data,
  verlage: async (f: VerbundVerlagFilter): Promise<VerbundSeite<VerbundVerlag>> => (await client.get('/verbund/verlage', { params: f })).data,
  verlag: async (slug: string): Promise<VerbundVerlagDetail> => (await client.get(`/verbund/verlage/${slug}`)).data,
  vereinbarungen: async (f: VerbundVereinbarungFilter): Promise<VerbundSeite<VerbundVereinbarung>> =>
    (await client.get('/verbund/vereinbarungen', { params: f })).data,
  vereinbarungVerlauf: async (id: string): Promise<VerbundVereinbarung[]> => (await client.get(`/verbund/vereinbarungen/${id}`)).data,
  sperren: async (alle: boolean): Promise<VerbundSperre[]> =>
    (await client.get('/verbund/sperren', { params: alle ? { alle: '1' } : {} })).data,
  verlauf: async (slug: string): Promise<{ verlag: { slug: string; name: string; status: VerlagStatus }; sperren: VerbundSperre[] }> =>
    (await client.get(`/verbund/verlage/${slug}/sperren`)).data,
  sperre: async (slug: string, b: { art: 'gesperrt' | 'ausgeschlossen'; grund: string; domains?: string[] }) =>
    (await client.post(`/verbund/verlage/${slug}/sperren`, b)).data,
  aufheben: async (slug: string, grund: string) => (await client.post(`/verbund/verlage/${slug}/aufheben`, { grund })).data,
  abgleich: async (): Promise<Array<{ nodeId: string; error?: string; count?: number }>> => (await client.post('/verbund/abgleich', {})).data,
  knotenAbgleich: async (id: string) => (await client.post(`/verbund/knoten/${id}/abgleich`, {})).data,
  instanz: async (instanceId: string): Promise<VerbundInstanzKnoten> => (await client.get(`/verbund/instanz/${instanceId}`)).data,
};

/** Woher eine URL bekannt ist. Siehe DomainUrl. */
export type UrlSource =
  | 'SITEMAP' | 'CRAWL' | 'WAYBACK' | 'COMMONCRAWL' | 'GSC' | 'BING' | 'SERP' | 'MANUAL';

export type UrlScanStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';

export interface DomainUrlSourceEintrag {
  source: UrlSource;
  detail?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface DomainUrl {
  id: string;
  url: string;
  path: string;
  firstSeenAt: string;
  lastSeenAt: string;
  statusCode?: number | null;
  checkedAt?: string | null;
  title?: string | null;
  redirectTo?: string | null;
  impressions?: number | null;
  clicks?: number | null;
  notes?: string | null;
  sources: DomainUrlSourceEintrag[];
}

/** Was eine Quelle in einem Durchlauf beigetragen hat. */
export interface QuellenBericht {
  quelle: UrlSource;
  gefunden: number;
  neu: number;
  gedeckelt: boolean;
  uebersprungen: number;
  fehler?: string;
  hinweis?: string;
  dauerMs: number;
}

export interface DomainUrlScan {
  id: string;
  domainId: string;
  status: UrlScanStatus;
  sources: UrlSource[];
  startedAt: string;
  finishedAt?: string | null;
  gefunden: number;
  neu: number;
  bericht?: QuellenBericht[] | null;
  error?: string | null;
}

/**
 * Die beiden Administratorkonten einer Instanz.
 *
 * `master` ist unser Betreiberkonto, `customer` das des Kunden. Beide kommen ohne
 * Passwort — das gibt der PC nach dem Anlegen nie wieder heraus.
 */
/**
 * Wo das Kennwert eines bestehenden Kontos gesetzt werden kann.
 *
 *   'hier'        in dieser Karte (ojfm: direkt in die Datenbank)
 *   'bryve-karte' im eigenen Abschnitt weiter unten (bryve: ueber dessen
 *                 Schnittstelle, und fuer jedes Admin-Konto)
 *   'keiner'      fuer dieses Produkt ist der Weg nicht bekannt
 */
export type DirektWeg = 'hier' | 'bryve-karte' | 'keiner';

export interface AdminKonto {
  /** Wo die Adresse in der Instanzkonfiguration steht, z. B. ADMIN_EMAIL. */
  emailField: string;
  email: string | null;
  secretKey: string;
  hasPassword: boolean;
  /** True, wenn nach der letzten Passwortänderung ausgerollt wurde. */
  deployed: boolean;
  /** False heisst: die App liest die Werte nur bei der Erstinstallation. */
  appliesOnDeploy: boolean;
}

export interface KundenAdminKonto {
  supported: boolean;
  email: string | null;
  /** Woher die Adresse stammt, damit klar ist, was zu ändern wäre. */
  emailSource: 'instanz' | 'technische Adresse' | 'Rechnungsadresse' | null;
  hasPassword: boolean;
}

export interface ZugangslinkErgebnis {
  adresse: string;
  modus: 'ANZEIGEN' | 'SETZEN';
  link: string;
  /** False heisst: kein SMTP eingerichtet, der Link steht nur im Protokoll. */
  gesendet: boolean;
  /** True, wenn an die eigene Adresse statt an den Kunden geschickt wurde. */
  probelauf: boolean;
}

/** Der Stand der Search Console für eine Domain. Siehe `gscStand`. */
export interface GscStand {
  eingerichtet: boolean;
  /** Die Adresse des Dienstkontos — die, die einzutragen ist. */
  adresse: string | null;
  erreichbar: boolean;
  /** Die gefundene Property, oder null. */
  property: string | null;
  /** Alle Properties, die das Konto sieht. */
  bekannte: string[];
  hinweis: string | null;
}

export interface DomainUrlStats {
  gesamt: number;
  jeQuelle: Partial<Record<UrlSource, number>>;
  letzterScan?: DomainUrlScan | null;
}

/** Was ein DNS-Import bewirkt hat — oder bewirken würde. */
export interface DnsImportBericht {
  format: 'bind' | 'tsv' | 'csv';
  dateiDomain?: string;
  eintraegeNeu: number;
  eintraegeAktualisiert: number;
  subdomainsNeu: number;
  subdomainsAktualisiert: number;
  fremd: Array<{ name: string; type: string }>;
  uebersprungen: Array<{ zeile: number; text: string; grund: string }>;
  proxiedAnzahl: number;
  cloudflareGesetzt: boolean;
  vorschau: boolean;
  subdomains: Array<{ fqdn: string; ziel: string; neu: boolean }>;
}

export interface Domain {
  id: string;
  name: string;
  /** Lesbare Form einer Umlautdomain; `name` trägt die Punycode-Form. */
  unicodeName?: string | null;
  tld?: string | null;
  providerId?: string | null;
  provider?: { id: string; name: string; slug: string } | null;
  status: DomainStatus;
  registrarTarget?: string | null;
  registeredAt?: string | null;
  expiresAt?: string | null;
  cloudflareTunnel: boolean;
  cloudflareDetected: boolean;
  monitorInterval: MonitorInterval;
  ownership: DomainOwnership;
  /** Vertragslaufzeit in Monaten. */
  termMonths?: number | null;
  /** Kündigungsfrist in Tagen vor `expiresAt`. */
  noticeDays?: number | null;
  notes?: string | null;
  lastDnsCheckAt?: string | null;
  lastCheckAt?: string | null;
  lastResult?: DomainCheckResult | null;
  records?: DnsRecord[];
  subdomains?: Subdomain[];
  checks?: DomainCheck[];
  _count?: { subdomains: number };
}

export const domains = {
  /** Alle Domains, ueber alle Seiten hinweg — die Tabelle sucht und sortiert selbst. */
  list: async (params?: Record<string, unknown>): Promise<Domain[]> =>
    alleSeiten<Domain>('/domains', params),
  get: async (id: string): Promise<Domain> => {
    const { data } = await client.get(`/domains/${id}`);
    return data;
  },
  create: async (payload: Partial<Domain> & { name: string }): Promise<Domain> => {
    const { data } = await client.post('/domains', payload);
    return data;
  },
  update: async (id: string, payload: Partial<Domain>): Promise<Domain> => {
    const { data } = await client.patch(`/domains/${id}`, payload);
    return data;
  },
  remove: async (id: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/domains/${id}`);
    return data;
  },
  stats: async (): Promise<Record<string, unknown>> => {
    const { data } = await client.get('/domains/stats');
    return data;
  },
  resolve: async (id: string): Promise<Record<string, unknown>> => {
    const { data } = await client.post(`/domains/${id}/resolve`);
    return data;
  },
  check: async (id: string): Promise<DomainCheck> => {
    const { data } = await client.post(`/domains/${id}/check`);
    return data;
  },
  addSubdomain: async (id: string, payload: { label: string; monitorInterval?: MonitorInterval; notes?: string }): Promise<Subdomain> => {
    const { data } = await client.post(`/domains/${id}/subdomains`, payload);
    return data;
  },
  updateSubdomain: async (subId: string, payload: Partial<Subdomain>): Promise<Subdomain> => {
    const { data } = await client.patch(`/domains/subdomains/${subId}`, payload);
    return data;
  },
  removeSubdomain: async (subId: string): Promise<{ message: string }> => {
    const { data } = await client.delete(`/domains/subdomains/${subId}`);
    return data;
  },
  checkSubdomain: async (subId: string): Promise<DomainCheck> => {
    const { data } = await client.post(`/domains/subdomains/${subId}/check`);
    return data;
  },
  placeholderSignatures: async (): Promise<Array<{ kind: string; beschreibung: string }>> => {
    const { data } = await client.get('/domains/placeholder-signatures');
    return data;
  },
  providers: {
    list: async (): Promise<HostingProvider[]> => {
      const { data } = await client.get('/domains/providers');
      return data;
    },
    create: async (payload: { name: string; slug: string; website?: string; notes?: string }): Promise<HostingProvider> => {
      const { data } = await client.post('/domains/providers', payload);
      return data;
    },
    update: async (id: string, payload: Partial<HostingProvider>): Promise<HostingProvider> => {
      const { data } = await client.patch(`/domains/providers/${id}`, payload);
      return data;
    },
    remove: async (id: string): Promise<{ message: string }> => {
      const { data } = await client.delete(`/domains/providers/${id}`);
      return data;
    },
  },
  /**
   * CSV-Ausgabe.
   *
   * Der Browser bekommt einen Blob und keinen Link: der Export hängt am
   * Anmelde-Token, und ein blosses `window.open` schickte die Anfrage ohne
   * Kopfzeile los und bekäme eine Anmeldeseite statt einer Datei.
   */
  // ─── URL-Bestandsaufnahme ─────────────────────────────────────────────────

  /**
   * URLs einer Domain — serverseitig geblättert.
   *
   * Anders als die Domainliste: eine einzelne Domain kann tausende URLs haben
   * (juni.com: 3275), und die alle in den Browser zu laden, um dort zu suchen,
   * wäre der falsche Tausch. Deshalb suchen und filtern hier Server und
   * Blätterleiste gemeinsam.
   */
  urls: async (
    domainId: string,
    params?: Record<string, unknown>,
  ): Promise<PaginatedResponse<DomainUrl>> => {
    const { data } = await client.get(`/domains/${domainId}/urls`, { params });
    return data;
  },

  urlStats: async (domainId: string): Promise<DomainUrlStats> => {
    const { data } = await client.get(`/domains/${domainId}/urls/stats`);
    return data;
  },

  urlScans: async (domainId: string): Promise<DomainUrlScan[]> => {
    const { data } = await client.get(`/domains/${domainId}/urls/scans`);
    return data;
  },

  /** Stösst einen Durchlauf an. Antwortet sofort — der Durchlauf dauert Minuten. */
  urlScanStarten: async (
    domainId: string,
    body: { quellen?: UrlSource[]; maxSeiten?: number; maxTiefe?: number; nurSeiten?: boolean },
  ): Promise<{ jobId: string; quellen: UrlSource[] }> => {
    const { data } = await client.post(`/domains/${domainId}/urls/scan`, body);
    return data;
  },

  /**
   * Die URLs als CSV — mit denselben Filtern wie die Liste.
   *
   * Die Filter waren bis zum 29.09.2026 nicht dabei, weder hier noch im
   * Backend: wer nach „hat Sitemap, aber kein Common Crawl" filterte und dann
   * exportierte, bekam alle URLs der Domain. Nichts daran sagte ihm das.
   */
  urlsExportCsv: async (
    domainId: string,
    filter?: { q?: string; quelle?: string; ohneQuelle?: string },
  ): Promise<Blob> => {
    const { data } = await client.get(`/domains/${domainId}/urls/export.csv`, {
      params: {
        q: filter?.q || undefined,
        quelle: filter?.quelle || undefined,
        ohneQuelle: filter?.ohneQuelle || undefined,
      },
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Dieselbe Aktion auf viele Domains.
   *
   * Schickt Ids und keinen Filter: die Tabelle filtert im Browser, teils nach
   * Dingen, die der Server nicht kennt. Wer stattdessen den Filter schickte,
   * träfe womöglich andere Domains als die angehakten — bei einer Aktion, die
   * Zustand verändert, ist das der gefährlichere Fehler.
   */
  massenAktion: async (body: {
    ids: string[];
    aktion: string;
    wert?: string;
  }): Promise<{ betroffen: number; angefragt: number; nichtGefunden: number; hinweis?: string }> => {
    const { data } = await client.post('/domains/aktionen', body);
    return data;
  },

  /** DNS-Einträge einer Domain. */
  dns: async (domainId: string): Promise<DnsRecord[]> => {
    const { data } = await client.get(`/domains/${domainId}/dns`);
    return data;
  },

  /**
   * Zonendatei oder Registrar-Export einlesen.
   *
   * `vorschau: true` schreibt nichts und meldet nur, was entstehen würde. Die
   * Oberfläche fragt immer erst so — eine Zonendatei kann Namen enthalten, die
   * niemand erwartet.
   */
  dnsImport: async (
    domainId: string,
    inhalt: string,
    vorschau: boolean,
  ): Promise<DnsImportBericht> => {
    const { data } = await client.post(`/domains/${domainId}/dns/import`, { inhalt, vorschau });
    return data;
  },

  /** Welche URL-Quellen gerade benutzbar sind — SERP hängt an einem Schlüssel. */
  urlQuellen: async (): Promise<{ verfuegbar: UrlSource[] }> => {
    const { data } = await client.get('/domains/urls/quellen');
    return data;
  },

  /**
   * Ob die Search Console diese Domain für uns freigibt — live gefragt.
   *
   * Nicht zwischenspeichern: eingetragen wird das Dienstkonto in der Search
   * Console, nicht hier. Ein gemerkter Wert wäre genau dann falsch, wenn er
   * gebraucht wird — direkt nachdem jemand es hinzugefügt hat.
   */
  gscStand: async (domainId: string): Promise<GscStand> => {
    const { data } = await client.get(`/domains/${domainId}/urls/gsc-stand`);
    return data;
  },

  /** CSV über die Filter des Servers. Ohne Angaben: alles. */
  exportCsv: async (params?: Record<string, unknown>): Promise<Blob> => {
    const { data } = await client.get('/domains/export.csv', { params, responseType: 'blob' });
    return data;
  },

  /**
   * CSV genau der übergebenen Domains.
   *
   * Die Tabelle filtert im Browser — teils nach Dingen, die der Server nicht
   * kennt (der Fristfilter wird gerechnet). Statt die Filter doppelt zu pflegen
   * und sie auseinanderlaufen zu lassen, schickt der Browser die Ids dessen,
   * was er anzeigt. Damit stimmt der Export mit der Ansicht überein.
   */
  exportCsvAuswahl: async (ids: string[]): Promise<Blob> => {
    const { data } = await client.post('/domains/export.csv', { ids }, { responseType: 'blob' });
    return data;
  },
};

// ── Nutzer in den Kundeninstanzen ────────────────────────────────────────────
//
// Zwei Begriffe, die nicht verwechselt werden duerfen:
//
//   `customerUsers`  Portalkonten — kaufmaennische Identitaet (Vertraege,
//                    Rechnungen, Kuendigung). Leben im PC.
//   `appNutzer`      Arbeitszugaenge in ojfm/bryve. Leben in der INSTANZ; der
//                    PC liest sie bei jedem Aufruf frisch und speichert nichts.
//
// Derselbe Mensch kann beides sein, keines oder nur eines. Verbunden werden sie
// NICHT ueber die E-Mail-Adresse: geteilte Postfaecher (`info@`, `lektorat@`)
// sind im CRM dieses Hauses der Normalfall.

export interface AppNutzer {
  id: string;
  email: string;
  name: string | null;
  /** In der Wortwahl des Produkts — hier wird nichts vereinheitlicht. */
  rolle: string | null;
  aktiv: boolean;
  abgeschaltetAm: string | null;
  /**
   * Vor Aenderungen ueber den PC geschuetzt — `admin@juni.com` (unser Weg in
   * jede Instanz, zugleich der Zugang des PC selbst) und
   * `system@ojfm.internal` (kein Mensch, sondern der Urheber maschineller
   * Vorgaenge). Die Oberflaeche bietet dafuer erst gar keine Knoepfe an.
   */
  geschuetzt?: boolean;
}

export interface RechteOption { wert: string; text: string }

export interface Rechtefeld {
  schluessel: string;
  titel: string;
  art: 'einzel' | 'mehrfach';
  optionen: RechteOption[];
  wert: string | string[] | null;
  hinweis?: string;
}

export interface AusscheidenErgebnis {
  kunde: string;
  email: string;
  ergebnisse: Array<{
    instanceId: string;
    stackName: string | null;
    produkt: string;
    ok: boolean;
    zustand: 'abgeschaltet' | 'war_schon_aus' | 'nicht_gefunden' | 'fehler';
    meldung?: string;
  }>;
}

export interface KundenUebersicht {
  apps: Array<{
    instanceId: string;
    stackName: string | null;
    produkt: string;
    erreichbar: boolean;
    meldung?: string;
  }>;
  zeilen: Array<{
    email: string;
    name: string | null;
    /** Vor Aenderungen ueber den PC geschuetzt — siehe AppNutzer.geschuetzt. */
    geschuetzt?: boolean;
    zugaenge: Array<{ instanceId: string; nutzerId: string; rolle: string | null; aktiv: boolean }>;
  }>;
}

export const appNutzer = {
  uebersicht: async (customerId: string): Promise<KundenUebersicht> => {
    const { data } = await client.get(`/kunden/${customerId}/nutzer`);
    return data;
  },
  liste: async (instanceId: string): Promise<{ produkt: string; nutzer: AppNutzer[] }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/nutzer`);
    return data;
  },
  rollen: async (instanceId: string): Promise<{ produkt: string; rollen: string[]; standard: string }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/nutzer/rollen`);
    return data;
  },
  // Der Name getrennt: bryve führt in seinem Feld `name` den Nachnamen und hat
  // Vorname und Anrede daneben. Produkte mit nur einem Namensfeld bekommen
  // beides zusammengesetzt — das entscheidet der Adapter im Backend.
  anlegen: async (
    instanceId: string,
    felder: { email: string; nachname: string; vorname?: string; anrede?: string; rolle?: string },
  ):
    Promise<{ neu: boolean; nutzer: AppNutzer; passwort?: string }> => {
    const { data } = await client.post(`/instanzen/${instanceId}/nutzer`, felder);
    return data;
  },
  schalten: async (instanceId: string, an: boolean, kennung: { email?: string; id?: string; grund?: string }):
    Promise<{ geaendert: boolean; nutzer: AppNutzer }> => {
    const pfad = an ? 'einschalten' : 'abschalten';
    const { data } = await client.post(`/instanzen/${instanceId}/nutzer/${pfad}`, kennung);
    return data;
  },
  rechte: async (instanceId: string, nutzerId: string):
    Promise<{ produkt: string; nutzer: AppNutzer; felder: Rechtefeld[] }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/nutzer/${nutzerId}/rechte`);
    return data;
  },
  setzeRecht: async (instanceId: string, nutzerId: string, schluessel: string, wert: string | string[] | null):
    Promise<{ produkt: string; nutzer: AppNutzer; felder: Rechtefeld[] }> => {
    const { data } = await client.put(`/instanzen/${instanceId}/nutzer/${nutzerId}/rechte`, { schluessel, wert });
    return data;
  },
  /**
   * Alle Zugaenge einer ADRESSE beim Kunden abschalten.
   *
   * Bewusst „Adresse" und nicht „Person": zusammengefuehrt wird ueber die
   * E-Mail, und ein geteiltes Postfach ist mehrere Menschen. Deshalb zeigt die
   * Oberflaeche vorher, was genau geschlossen wird.
   */
  ausscheiden: async (customerId: string, email: string, grund?: string): Promise<AusscheidenErgebnis> => {
    const { data } = await client.post(`/kunden/${customerId}/nutzer/ausscheiden`, { email, grund });
    return data;
  },
};

// ── Feedback-Briefkasten ─────────────────────────────────────────────────────
//
// Ruecklaufpakete aus den Kundeninstallationen. Der PC nimmt an und haelt fest;
// geoeffnet werden sie in der Sammelstelle (open.junixx), nicht hier.

export interface FeedbackEingang {
  id: string;
  checksum: string;
  transport: string;
  surfaceId: string | null;
  kampagneName: string | null;
  fassungNr: number | null;
  antwortenAnz: number | null;
  receivedAt: string;
  fetchedAt: string | null;
  instance: { id: string; stackName: string | null; subdomain: string | null; hostingMode: string };
  customer: { id: string; companyName: string };
}

export const feedbackBriefkasten = {
  liste: async (nurOffen?: boolean): Promise<{ eintraege: FeedbackEingang[]; anzahl: number }> => {
    const { data } = await client.get('/feedback/ruecklauf', {
      params: nurOffen ? { offen: 'true' } : undefined,
    });
    return data;
  },
};

// ─── Datensicherung ──────────────────────────────────────────────────────────

export type SicherungsStatus = 'LAEUFT' | 'OK' | 'FEHLER' | 'WEGGERAEUMT';
export type SicherungsAusloeser = 'MANUELL' | 'PLAN' | 'VOR_UPDATE';
export type SicherungsZielArt = 'LOKAL' | 'SFTP' | 'FTP' | 'S3' | 'WEBDAV';

/**
 * Ein Ort, an den Sicherungen geschrieben werden.
 *
 * Das Geheimnis steht hier bewusst nicht: es geht verschluesselt hinein und
 * kommt nie zurueck. `hatGeheimnis` sagt nur, ob eines hinterlegt ist.
 */
export interface SicherungsZiel {
  id: string;
  name: string;
  art: SicherungsZielArt;
  pfad: string;
  host: string | null;
  port: number | null;
  benutzer: string | null;
  optionen: Record<string, unknown>;
  aktiv: boolean;
  hatGeheimnis: boolean;
  zuletztGeprueft: string | null;
  zuletztOk: boolean | null;
  letzterFehler: string | null;
  /** Wem das Ziel gehoert. Null heisst: unseres, fuer alle nutzbar. */
  customerId: string | null;
  customer: { id: string; companyName: string; shortCode: string | null } | null;
  /** Welche Instanzen darauf schreiben — und ueber welchen Plan. */
  verwendetVon: Array<{ id: string; stackName: string | null; ueber: string }>;
}

export interface SicherungsZielEingabe {
  name: string;
  art: SicherungsZielArt;
  pfad?: string;
  host?: string | null;
  port?: number | null;
  benutzer?: string | null;
  /** Nur mitschicken, wenn es sich aendern soll — leer heisst unveraendert. */
  geheimnis?: string;
  customerId?: string | null;
  optionen?: Record<string, unknown>;
  aktiv?: boolean;
}

export const sicherungsziele = {
  list: async (): Promise<SicherungsZiel[]> => {
    const { data } = await client.get('/sicherungsziele');
    return data;
  },
  anlegen: async (eingabe: SicherungsZielEingabe): Promise<SicherungsZiel> => {
    const { data } = await client.post('/sicherungsziele', eingabe);
    return data;
  },
  aendern: async (id: string, eingabe: Partial<SicherungsZielEingabe>): Promise<SicherungsZiel> => {
    const { data } = await client.put(`/sicherungsziele/${id}`, eingabe);
    return data;
  },
};

export interface Sicherung {
  id: string;
  dienst: string;
  motor: string;
  ausloeser: SicherungsAusloeser;
  status: SicherungsStatus;
  pfad: string | null;
  groesseBytes: number | null;
  sha256: string | null;
  begonnenAm: string;
  fertigAm: string | null;
  dauerSek: number | null;
  fehler: string | null;
  ablaufAm: string | null;
  actor: string | null;
  /** Wird beim automatischen Aufraeumen uebersprungen. */
  geschuetzt: boolean;
  ziel: { id: string; name: string; art: SicherungsZielArt } | null;
}

export interface SicherungsPlan {
  id: string;
  aktiv: boolean;
  intervallMinuten: number;
  startMinute: number | null;
  /** So viele bleiben. */
  behalteAnzahl: number;
  /** Alles Ältere geht — null heißt: kein Höchstalter. */
  loeschenNachTagen: number | null;
  /** Nichts automatisch löschen; nur ein Administrator räumt dann weg. */
  loeschschutz: boolean;
  maxBelegungMb: number | null;
  zielId: string | null;
  zuletztGelaufen: string | null;
  /** Woher der Plan kommt — an der Instanz gesetzt oder vom Kunden geerbt. */
  ebene?: 'instanz' | 'kunde';
}

export const sicherungen = {
  /** Historie, geltender Plan und die Datenbanken, die es zu sichern gibt. */
  list: async (
    instanceId: string,
  ): Promise<{
    sicherungen: Sicherung[];
    plan: SicherungsPlan | null;
    datenbanken: Array<{ dienst: string; motor: string }>;
  }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/sicherungen`);
    return data;
  },

  /** Jetzt sichern. Läuft im Aufruf, nicht als Auftrag — ein Dump dauert Sekunden. */
  jetzt: async (
    instanceId: string,
  ): Promise<{
    ergebnisse: Array<{ ok: boolean; pfad?: string; groesseBytes?: number; dauerSek: number; fehler?: string }>;
    meldung: string;
  }> => {
    const { data } = await client.post(`/instanzen/${instanceId}/sicherungen`);
    return data;
  },

  entfernen: async (instanceId: string, sicherungId: string): Promise<{ message?: string }> => {
    const { data } = await client.delete(`/instanzen/${instanceId}/sicherungen/${sicherungId}`);
    return data;
  },

  planSetzen: async (
    instanceId: string,
    plan: Partial<Omit<SicherungsPlan, 'id' | 'zuletztGelaufen' | 'ebene'>>,
  ): Promise<SicherungsPlan> => {
    const { data } = await client.put(`/instanzen/${instanceId}/sicherungsplan`, plan);
    return data;
  },

  /** Den eigenen Plan entfernen — danach gilt wieder der des Kunden. */
  planEntfernen: async (instanceId: string): Promise<{ message?: string }> => {
    const { data } = await client.delete(`/instanzen/${instanceId}/sicherungsplan`);
    return data;
  },

  ziele: async (): Promise<SicherungsZiel[]> => {
    const { data } = await client.get('/sicherungsziele');
    return data;
  },

  /**
   * Die Adresse zum Herunterladen.
   *
   * Als Link und nicht als Abruf im Code: der Browser soll die Datei selbst
   * entgegennehmen. Ein Abruf über axios hielte eine mehrere Gigabyte große
   * Antwort erst vollständig im Speicher, bevor irgendetwas sichtbar wird.
   */
  downloadUrl: (instanceId: string, sicherungId: string): string =>
    `${client.defaults.baseURL}/instanzen/${instanceId}/sicherungen/${sicherungId}/download`,

  /** Eine einzelne Sicherung vor dem automatischen Aufraeumen schuetzen. */
  schutzSetzen: async (
    instanceId: string,
    sicherungId: string,
    geschuetzt: boolean,
  ): Promise<{ id: string; geschuetzt: boolean }> => {
    const { data } = await client.patch(
      `/instanzen/${instanceId}/sicherungen/${sicherungId}/schutz`,
      { geschuetzt },
    );
    return data;
  },

  /**
   * Die Verbindungsdaten zur Datenbank — fuer Werkzeuge, die nicht pgAdmin
   * sind. Das Passwort ist enthalten; der Abruf wird protokolliert.
   */
  datenbankzugang: async (
    instanceId: string,
  ): Promise<{
    wirt: string | null;
    tunnelAdresse: string;
    zugaenge: Array<{
      dienst: string; motor: string; container: string; datenbank: string;
      benutzer: string; passwort: string | null; intern: string;
      loopbackPort: string | null;
    }>;
  }> => {
    const { data } = await client.get(`/instanzen/${instanceId}/datenbankzugang`);
    return data;
  },

  /** Zurückspielen. `bestaetigung` ist der Stackname — die Route prüft ihn. */
  einspielen: async (
    instanceId: string,
    sicherungId: string,
    bestaetigung: string,
  ): Promise<{ ok: boolean; meldung: string; sicherungVorher?: string }> => {
    const { data } = await client.post(
      `/instanzen/${instanceId}/sicherungen/${sicherungId}/einspielen`,
      { bestaetigung },
    );
    return data;
  },
};

// ─── Bereitschaft und Eskalation ─────────────────────────────────────────────

export interface BereitschaftsMitglied {
  id?: string;
  userId?: string | null;
  name: string;
  email: string;
  slackId?: string | null;
  prioritaet: number;
  wartezeitMinuten: number;
  aktiv: boolean;
}

export interface BereitschaftsStufe {
  id?: string;
  ordnung: number;
  bezeichnung: string;
  nachMinuten: number;
  mitglieder: BereitschaftsMitglied[];
}

export type ZeitregelArt = 'NACHT' | 'WOCHENENDE' | 'FEIERTAG' | 'ZEITRAUM';

export interface FeiertagEintrag {
  schluessel: string;
  name: string;
  mmdd: string;
  regional: boolean;
}

export interface BereitschaftsZeitregel {
  id?: string;
  bezeichnung: string;
  art: ZeitregelArt;
  vonStunde?: number | null;
  bisStunde?: number | null;
  vonDatum?: string | null;
  bisDatum?: string | null;
  bundesland?: string | null;
  fristFaktor: number;
  schwereAbsenkenAuf?: 'critical' | 'warning' | 'info' | null;
  ausgesetzt: boolean;
  aktiv: boolean;
  /** Statt wecken: erst ab dieser Stunde zustellen. */
  aufschiebenBisStunde?: number | null;
  /** Welche Feiertage konkret. Leer = alle des Bundeslands. */
  feiertagsSchluessel?: string[] | null;
}

export interface Bereitschaftsplan {
  id: string;
  name: string;
  beschreibung?: string | null;
  aktiv: boolean;
  istVorgabe: boolean;
  giltFuerSchwere: string[];
  serverId?: string | null;
  customerId?: string | null;
  wiederholungMinuten: number;
  maxWiederholungen: number;
  stufen: BereitschaftsStufe[];
  zeitregeln: BereitschaftsZeitregel[];
  server?: { id: string; hostname: string } | null;
  customer?: { id: string; companyName: string } | null;
}

export interface EskalationsZustellung {
  id: string;
  empfaengerName: string;
  empfaengerEmail?: string | null;
  stufe: number;
  prioritaet: number;
  kanal: 'MAIL' | 'SLACK' | 'PC';
  erfolg: boolean;
  fehler?: string | null;
  gesendetAm: string;
}

export interface Eskalationslauf {
  id: string;
  alertId: string;
  zustand: 'LAEUFT' | 'UEBERNOMMEN' | 'BEENDET' | 'ABGEBROCHEN';
  aktuelleStufe: number;
  aktuellePrioritaet: number;
  wiederholungen: number;
  naechsterSchrittAm?: string | null;
  fristFaktor: number;
  gemeldeteSchwere: string;
  zeitregelName?: string | null;
  uebernommenAm?: string | null;
  uebernahmeNotiz?: string | null;
  createdAt: string;
  alert: {
    id: string;
    name: string;
    summary: string;
    severity: string;
    status: string;
    startsAt: string;
    serverId?: string | null;
    instanceId?: string | null;
  };
  plan: { id: string; name: string };
  uebernommenUser?: { id: string; name: string; email: string } | null;
  zustellungen: EskalationsZustellung[];
}

export interface EskalationsVorschau {
  zeitpunkt: string;
  lage: { faktor: number; schwere: string; ausgesetzt: boolean; regelName: string | null };
  ausgesetzt: boolean;
  hinweis?: string;
  wiederholungAlle?: number;
  schritte: Array<{
    minute: number;
    zeit: string;
    stufe: number;
    stufeName: string;
    wer: string[];
    was: string;
  }>;
}


export interface EskalationsEintrag {
  id: string;
  art: 'START' | 'ESKALATION' | 'ZUSTELLUNG' | 'UEBERNAHME' | 'NOTIZ' | 'WEITERGABE' | 'ABSCHLUSS';
  autorName?: string | null;
  text: string;
  createdAt: string;
  user?: { id: string; name: string } | null;
}

export type Ampel = 'gruen' | 'gelb' | 'rot' | 'unbestimmt';

export interface PersonAuslastung {
  userId: string | null;
  name: string;
  email: string | null;
  ansprachen: number;
  ausserhalb: number;
  ruhetage: number;
  uebernahmen: number;
  last: number;
  /** Vielfaches des Mittels aller Beteiligten. */
  anteil: number;
  ampel: Ampel;
  inPlaenen: number;
  letzteAnsprache: string | null;
}

export interface AuslastungsBericht {
  vonDatum: string;
  bisDatum: string;
  personen: PersonAuslastung[];
  mittel: number;
  ereignisse: number;
  /** Zu wenig Daten fuer eine belastbare Aussage. */
  duenn: boolean;
  hinweis: string | null;
}

export interface DienstProtokollEintrag {
  zeitpunkt: string;
  art: 'ansprache' | 'uebernahme';
  person: string;
  email: string | null;
  userId: string | null;
  anlass: string | null;
  plan: string | null;
  stufe: number | null;
  kanal: string | null;
  lage: 'geschaeftszeit' | 'ausserhalb' | 'ruhetag';
  notiz: string | null;
}

export const bereitschaft = {
  /**
   * Wie sich die Last verteilt. Zaehlt, wie oft jemand herangezogen wurde —
   * nicht, wie er sich dabei geschlagen hat.
   */
  auslastung: async (tage = 90): Promise<AuslastungsBericht> => {
    const { data } = await client.get('/bereitschaft/auslastung', { params: { tage } });
    return data;
  },
  /** Wer wann Bereitschaft getragen hat, chronologisch. */
  dienstprotokoll: async (tage = 90): Promise<DienstProtokollEintrag[]> => {
    const { data } = await client.get('/bereitschaft/dienstprotokoll', { params: { tage } });
    return data;
  },
  plaene: async (): Promise<Bereitschaftsplan[]> => {
    const { data } = await client.get('/bereitschaft/plaene');
    return data;
  },
  plan: async (id: string): Promise<Bereitschaftsplan> => {
    const { data } = await client.get(`/bereitschaft/plaene/${id}`);
    return data;
  },
  anlegen: async (plan: Partial<Bereitschaftsplan>): Promise<Bereitschaftsplan> => {
    const { data } = await client.post('/bereitschaft/plaene', plan);
    return data;
  },
  aendern: async (id: string, plan: Partial<Bereitschaftsplan>): Promise<Bereitschaftsplan> => {
    const { data } = await client.put(`/bereitschaft/plaene/${id}`, plan);
    return data;
  },
  loeschen: async (id: string): Promise<void> => {
    await client.delete(`/bereitschaft/plaene/${id}`);
  },
  /** Rechnet den Ablauf durch, ohne jemanden zu wecken. */
  vorschau: async (
    id: string,
    params?: { zeitpunkt?: string; schwere?: string },
  ): Promise<EskalationsVorschau> => {
    const { data } = await client.get(`/bereitschaft/plaene/${id}/vorschau`, { params });
    return data;
  },
  feiertage: async (
    jahr: number,
    bundesland?: string,
  ): Promise<{ jahr: number; tage: string[]; benannt: FeiertagEintrag[] }> => {
    const { data } = await client.get('/bereitschaft/feiertage', { params: { jahr, bundesland } });
    return data;
  },
  /**
   * Probelauf. `zustellung` schickt jedem eine Testmeldung, `eskalation`
   * laesst die echte Kette im Zeitraffer durchlaufen.
   */
  probelauf: async (
    id: string,
    modus: 'zustellung' | 'eskalation',
  ): Promise<{
    modus: string;
    plan: string;
    zugestellt?: Array<{
      name: string;
      stufe: number;
      mail: boolean;
      slack: boolean;
      pc: boolean;
      hinweis?: string;
    }>;
    alertId?: string;
    raffung?: number;
    endetUm?: string;
  }> => {
    const { data } = await client.post(`/bereitschaft/plaene/${id}/probelauf`, { modus });
    return data;
  },
  laeufe: async (nurOffene = true): Promise<Eskalationslauf[]> => {
    const { data } = await client.get('/bereitschaft/laeufe', {
      params: { nurOffene: nurOffene ? 'true' : 'false' },
    });
    return data;
  },
  uebernehmen: async (alertId: string, notiz?: string): Promise<Eskalationslauf> => {
    const { data } = await client.post(`/bereitschaft/alarme/${alertId}/uebernehmen`, { notiz });
    return data;
  },
  abbrechen: async (alertId: string): Promise<void> => {
    await client.post(`/bereitschaft/alarme/${alertId}/abbrechen`);
  },
  /** Das Vorfallsprotokoll, aelteste Zeile zuerst. */
  protokoll: async (alertId: string): Promise<EskalationsEintrag[]> => {
    const { data } = await client.get(`/bereitschaft/alarme/${alertId}/protokoll`);
    return data;
  },
  notiz: async (alertId: string, text: string): Promise<void> => {
    await client.post(`/bereitschaft/alarme/${alertId}/notiz`, { text });
  },
  /** Uebernahme zurueckgeben — die Kette laeuft weiter statt zu enden. */
  weitergeben: async (alertId: string, text: string): Promise<void> => {
    await client.post(`/bereitschaft/alarme/${alertId}/weitergeben`, { text });
  },
  starten: async (alertId: string): Promise<void> => {
    await client.post(`/bereitschaft/alarme/${alertId}/starten`);
  },
};

// ─── Benachrichtigungen im PC ────────────────────────────────────────────────

export interface PcBenachrichtigung {
  id: string;
  schwere: string;
  titel: string;
  text?: string | null;
  link?: string | null;
  quelle?: string | null;
  gelesenAm?: string | null;
  createdAt: string;
}

export const benachrichtigungen = {
  liste: async (params?: {
    ungelesen?: boolean;
    limit?: number;
  }): Promise<{ eintraege: PcBenachrichtigung[]; ungelesen: number }> => {
    const { data } = await client.get('/benachrichtigungen', {
      params: { ungelesen: params?.ungelesen ? 'true' : undefined, limit: params?.limit },
    });
    return data;
  },
  anzahl: async (): Promise<{ ungelesen: number }> => {
    const { data } = await client.get('/benachrichtigungen/anzahl');
    return data;
  },
  gelesen: async (id: string): Promise<void> => {
    await client.post(`/benachrichtigungen/${id}/gelesen`);
  },
  alleGelesen: async (): Promise<void> => {
    await client.post('/benachrichtigungen/alle-gelesen');
  },
};

// ─── Mail-Gestaltung ─────────────────────────────────────────────────────────

export interface Schriftzug {
  familie?: string;
  groesse?: number;
  farbe?: string;
  schnitt?: number;
  abstandOben?: number;
  abstandUnten?: number;
  zeilenhoehe?: number;
  ausrichtung?: 'left' | 'center' | 'right';
}

export interface MailWerte {
  ueberschriften: { h1?: Schriftzug; h2?: Schriftzug; h3?: Schriftzug; h4?: Schriftzug; h5?: Schriftzug };
  fliesstext: Schriftzug;
  inhalt: { polsterOben?: number; polsterUnten?: number; polsterSeitlich?: number; ausrichtung?: 'left' | 'center' | 'right' };
  hintergrund: { seite?: string; karte?: string; rahmen?: string; eckenRadius?: number; abstandAussen?: number; maxBreite?: number };
  logo: { breite?: number; hoehe?: number; ausrichtung?: 'left' | 'center' | 'right' };
  knopf: { hintergrund?: string; schrift?: string; eckenRadius?: number };
  akzent: { farbe?: string; strichBreite?: number };
  fusszeile: {
    text?: string; groesse?: number; farbe?: string; rechtstext?: string;
    impressumUrl?: string; datenschutzUrl?: string; impressumText?: string; datenschutzText?: string;
  };
}

export interface MailGestaltungStand {
  geltend: {
    werte: MailWerte;
    absenderName: string | null;
    logoQuelle: string | null;
    herkunft: string[];
    ueberschrieben: string[];
  };
  eigene: {
    werte: unknown;
    absenderName: string | null;
    logoQuelle: string | null;
    aktiv: boolean;
  } | null;
  vomKunden: { kundeName: string | null; werte: unknown } | null;
}

export const mailGestaltung = {
  vorgabe: async (): Promise<{ werte: MailWerte }> => {
    const { data } = await client.get('/mail-gestaltung/vorgabe');
    return data;
  },
  lesen: async (art: 'kunde' | 'instanz', id: string): Promise<MailGestaltungStand> => {
    const { data } = await client.get(`/mail-gestaltung/${art}/${id}`);
    return data;
  },
  /** Serverseitig gerendert — dieselbe Huelle wie beim echten Versand. */
  vorschau: async (
    art: 'kunde' | 'instanz',
    id: string,
    entwurf: { werte?: unknown; absenderName?: string | null; logoQuelle?: string | null },
  ): Promise<{ html: string; herkunft: string[]; ueberschrieben: string[] }> => {
    const { data } = await client.post(`/mail-gestaltung/${art}/${id}/vorschau`, entwurf);
    return data;
  },
  speichern: async (
    art: 'kunde' | 'instanz',
    id: string,
    daten: { werte?: unknown; absenderName?: string | null; logoQuelle?: string | null; aktiv?: boolean },
  ): Promise<void> => {
    await client.put(`/mail-gestaltung/${art}/${id}`, daten);
  },
  zuruecksetzen: async (art: 'kunde' | 'instanz', id: string): Promise<void> => {
    await client.delete(`/mail-gestaltung/${art}/${id}`);
  },
};

// ─── Git (Forgejo) ──────────────────────────────────────────────────────────

export interface ForgejoStatus {
  eingerichtet: boolean;
  erreichbar?: boolean;
  meldung: string;
  adresse: string | null;
  organisation?: string | null;
  konten: { gesamt: number; aktiv: number; gesperrt: number };
}

export interface ForgejoKontoZeile {
  userId: string;
  email: string;
  name: string;
  rolle: string;
  pcStatus: string;
  konto: {
    benutzername: string;
    aktiv: boolean;
    forgejoId: number | null;
    schluessel: string | null;
    angelegtAm: string;
    gesperrtAm: string | null;
  } | null;
  /** Was zwischen PC und Git auseinanderläuft — der Grund, warum es die Seite gibt. */
  abweichung: string | null;
}

export interface ForgejoErgebnis {
  ok: boolean;
  meldung: string;
  benutzername?: string;
}

export const forgejo = {
  status: async (): Promise<ForgejoStatus> => {
    const { data } = await client.get('/forgejo/status');
    return data;
  },
  konten: async (): Promise<{ zeilen: ForgejoKontoZeile[]; abweichungen: number }> => {
    const { data } = await client.get('/forgejo/konten');
    return data;
  },
  anlegen: async (userId: string): Promise<ForgejoErgebnis> => {
    const { data } = await client.post(`/forgejo/konten/${userId}/anlegen`);
    return data;
  },
  sperren: async (userId: string): Promise<ForgejoErgebnis> => {
    const { data } = await client.post(`/forgejo/konten/${userId}/sperren`);
    return data;
  },
  entsperren: async (userId: string): Promise<ForgejoErgebnis> => {
    const { data } = await client.post(`/forgejo/konten/${userId}/entsperren`);
    return data;
  },
  entfernen: async (userId: string): Promise<ForgejoErgebnis> => {
    const { data } = await client.delete(`/forgejo/konten/${userId}`);
    return data;
  },
  schluessel: async (userId: string, schluessel: string, titel?: string): Promise<ForgejoErgebnis> => {
    const { data } = await client.post(`/forgejo/konten/${userId}/schluessel`, { schluessel, titel });
    return data;
  },
  /** Gibt es in Forgejo schon ein Konto mit dieser Adresse? */
  suche: async (email: string): Promise<{
    gefunden: boolean;
    konto?: { benutzername: string; id: number | null } | null;
    meldung?: string;
  }> => {
    const { data } = await client.get('/forgejo/suche', { params: { email } });
    return data;
  },
  /**
   * Kennwort setzen. Wird nicht gespeichert — es geht einmal nach Forgejo und
   * einmal an den, der es setzt.
   */
  kennwort: async (
    userId: string,
    kennwort: string,
    aendernErzwingen = false,
  ): Promise<ForgejoErgebnis> => {
    const { data } = await client.post(`/forgejo/konten/${userId}/kennwort`, {
      kennwort,
      aendernErzwingen,
    });
    return data;
  },
  abgleich: async (): Promise<{
    ok: boolean;
    geaendert: number;
    meldung: string;
    ergebnisse: Array<{ email: string; tat: string; ok: boolean; meldung: string }>;
  }> => {
    const { data } = await client.post('/forgejo/abgleich');
    return data;
  },
};

// ─── Registry-Pflege ────────────────────────────────────────────────────────

export interface Staffelung {
  alleTage: number;
  taeglichTage: number;
  woechentlichTage: number;
}

export interface TagBefund {
  tag: string;
  digest?: string;
  erstellt?: string;
  groesseBytes?: number;
  behalten: boolean;
  grund: string;
}

export interface RepoBefund {
  repository: string;
  tags: TagBefund[];
  behalten: number;
  loeschen: number;
  summeBytes: number;
}

export interface PflegeBericht {
  repositories: RepoBefund[];
  behalten: number;
  loeschen: number;
  /**
   * Summe der Abzugsgrössen — NICHT der frei werdende Platz, meist ein
   * Vielfaches davon, weil geteilte Schichten je Abzug voll zählen. Was
   * wirklich frei wird, sagt erst die Müllabfuhr.
   */
  summeBytes: number;
  geschuetzt: string[];
  fehler: string[];
}

export interface MuellBericht {
  container: string | null;
  platteVorher: string | null;
  platteNachher: string | null;
  freigegeben: string | null;
  ausgabe: string;
  nurVorschau: boolean;
}

export const registryPflege = {
  status: async (): Promise<{ configured: boolean; url?: string; reachable?: boolean; message?: string }> => {
    const { data } = await client.get('/registry/status');
    return data;
  },
  repositories: async (): Promise<{ host: string | null; repositories: string[] }> => {
    const { data } = await client.get('/registry/repositories');
    return data;
  },
  /** Teuer — je Abzug mehrere Abrufe. Läuft nur auf Knopfdruck. */
  vorschau: async (regeln: Staffelung): Promise<PflegeBericht> => {
    const { data } = await client.post('/registry/pflege/vorschau', regeln);
    return data;
  },
  ausfuehren: async (
    regeln: Staffelung,
  ): Promise<{ geloescht: number; fehlgeschlagen: number; fehler: string[]; hinweis: string }> => {
    const { data } = await client.post('/registry/pflege/ausfuehren', regeln);
    return data;
  },
  muell: async (serverId: string, nurVorschau: boolean): Promise<MuellBericht> => {
    const { data } = await client.post('/registry/pflege/muell', { serverId, nurVorschau });
    return data;
  },
};

// ─── Zugang zum Proxy-Panel ─────────────────────────────────────────────────

export interface ProxyZugangZustand {
  vorhanden: boolean;
  email: string | null;
  kennwortGesetzt: boolean;
  auslieferungszustand: boolean;
  passtZumHauptadmin: boolean;
  hauptadmin: { userId: string; email: string; name: string } | null;
  hinweis: string | null;
}

export interface ProxyZugangErgebnis {
  ok: boolean;
  meldung: string;
  /** Nur bei frisch erzeugtem Kennwort, genau einmal. */
  kennwort?: string;
  schritte: Array<{ ort: string; ok: boolean; meldung: string }>;
}

export const proxyZugang = {
  lesen: async (serverId: string): Promise<ProxyZugangZustand> => {
    const { data } = await client.get(`/servers/${serverId}/proxy-zugang`);
    return data;
  },
  setzen: async (serverId: string, kennwort?: string): Promise<ProxyZugangErgebnis> => {
    const { data } = await client.post(`/servers/${serverId}/proxy-zugang`, { kennwort });
    return data;
  },
};

// ─── Analytics: Reichweite und Erreichbarkeit aller Seiten ──────────────────

export interface SeitenZahlen {
  instanceId: string;
  nummer: number | null;
  name: string;
  domain: string | null;
  kunde: string | null;
  art: string;
  status: string;
  /** Misst diese Instanz überhaupt? */
  misst: boolean;
  aufrufe: number;
  besuche: number;
  aufrufeVorher: number;
  besucheVorher: number;
  /** Veränderung in Prozent, oder null wenn vorher nichts da war. */
  wandelProzent: number | null;
  einzelseitenAnteil: number | null;
  dauerSchnittMs: number | null;
  botsVerworfen: number;
  verlauf: number[];
}

export interface AnalyticsSeiten {
  von: string;
  bis: string;
  tage: number;
  seiten: SeitenZahlen[];
  summe: { aufrufe: number; besuche: number; aufrufeVorher: number; besucheVorher: number };
}

/**
 * Derselbe Befund wie in der Adressuebersicht einer Instanz -- gruen
 * antwortet, gelb antwortet mit Einschraenkung (geschuetzt, Weiterleitung
 * woandershin), rot antwortet nicht.
 */
export interface ErreichbarkeitsBefund {
  instanceId: string;
  hostname: string;
  ampel: 'gruen' | 'gelb' | 'rot';
  status: number | null;
  ziel: string | null;
  befund: string;
  dauerMs: number;
}

export const analytics = {
  seiten: async (zeitraum = '28d'): Promise<AnalyticsSeiten> => {
    const { data } = await client.get('/analytics/seiten', { params: { zeitraum } });
    return data;
  },
  /**
   * Ruft die Seiten tatsächlich ab — deshalb POST und nur auf Knopfdruck.
   * Bei zwanzig Instanzen dauert das einige Sekunden.
   */
  erreichbarkeit: async (instanceIds?: string[]): Promise<ErreichbarkeitsBefund[]> => {
    const { data } = await client.post('/analytics/erreichbarkeit', { instanceIds });
    return data;
  },
};

// ─── Dashboard ──────────────────────────────────────────────────────────────

export interface DashboardBereich {
  schluessel: string;
  titel: string;
  zahl: number | string | null;
  einheit: string;
  hinweis: string | null;
  /** 0 ruhig, 1 hinsehen, 2 jetzt. Bestimmt die Reihenfolge. */
  dringlichkeit: 0 | 1 | 2;
  pfad: string;
  eintraege: Array<{ id: string; text: string; neben?: string; pfad?: string }>;
  fehler: string | null;
}

export const dashboard = {
  uebersicht: async (): Promise<{ bereiche: DashboardBereich[]; erzeugtAm: string }> => {
    const { data } = await client.get('/dashboard/uebersicht');
    return data;
  },
};

// ─── Bitwarden (Tresor-Sammlung der Organisation) ──────────────────────────

export const bitwarden = {
  status: async (): Promise<{ eingerichtet: boolean; ok?: boolean; automatisch?: boolean; meldung: string }> => {
    const { data } = await client.get('/bitwarden/status');
    return data;
  },
  /**
   * Alle Zugänge einer Instanz (Betreiberkonto, Kunden-Admin) als Login-Eintrag
   * ablegen bzw. aktualisieren. Maßgeblich bleibt der PC.
   */
  ablegen: async (instanceId: string): Promise<{ ok: boolean; meldung: string }> => {
    const { data } = await client.post(`/bitwarden/instanz/${instanceId}`);
    return data;
  },
};

// ─── SSH-Schlüssel je Mensch ────────────────────────────────────────────────

export interface BenutzerSchluessel {
  id: string;
  userId: string;
  name: string;
  publicKey: string;
  fingerprint: string;
  aktiv: boolean;
  widerrufenAm: string | null;
  widerrufsGrund: string | null;
  erstelltAm: string;
  zuletztVerteiltAm: string | null;
}

export interface SchluesselAusschluss {
  userId: string;
  serverId: string;
  grund: string | null;
  gesetztAm: string;
  server: { hostname: string };
}

export interface BenutzerFreigabe {
  id: string;
  userId: string;
  adresse: string;
  notiz: string | null;
  aktiv: boolean;
  erstelltAm: string;
  entferntAm: string | null;
}

export interface VerteilErgebnis {
  serverId: string;
  hostname: string;
  ok: boolean;
  meldung: string;
  anzahl?: number;
}

export const benutzerSchluessel = {
  lesen: async (
    userId: string,
  ): Promise<{
    schluessel: BenutzerSchluessel[];
    ausschluesse: SchluesselAusschluss[];
    freigaben: BenutzerFreigabe[];
    serverMitZugang: Array<{ id: string; hostname: string }>;
  }> => {
    const { data } = await client.get(`/benutzer-schluessel/${userId}`);
    return data;
  },
  /** Der private Teil kommt genau einmal zurück — danach ist er fort. */
  erzeugen: async (
    userId: string,
    name: string,
    passphrase?: string,
  ): Promise<{ ok: boolean; meldung: string; privaterSchluessel?: string; id?: string }> => {
    const { data } = await client.post(`/benutzer-schluessel/${userId}/erzeugen`, {
      name,
      ...(passphrase ? { passphrase } : {}),
    });
    return data;
  },
  hinterlegen: async (
    userId: string,
    name: string,
    publicKey: string,
  ): Promise<{ ok: boolean; meldung: string }> => {
    const { data } = await client.post(`/benutzer-schluessel/${userId}/hinterlegen`, {
      name,
      publicKey,
    });
    return data;
  },
  widerrufen: async (
    id: string,
    grund?: string,
  ): Promise<{ ok: boolean; meldung: string; ergebnisse: VerteilErgebnis[] }> => {
    const { data } = await client.post(`/benutzer-schluessel/schluessel/${id}/widerrufen`, {
      grund,
    });
    return data;
  },
  /** Auf allen Servern den Soll-Zustand herstellen. Dauert. Nur ADMIN. */
  abgleich: async (): Promise<{ ergebnisse: VerteilErgebnis[] }> => {
    const { data } = await client.post('/benutzer-schluessel/abgleich');
    return data;
  },
  /**
   * Selbstbedienung: den eigenen Schlüssel auf die Server bringen, auf die man
   * darf. Erweitert keine Rechte — geschrieben wird der Soll-Zustand, und
   * Ausschlüsse gelten unverändert.
   */
  meineVerteilen: async (): Promise<{ ergebnisse: VerteilErgebnis[] }> => {
    const { data } = await client.post('/benutzer-schluessel/mein/verteilen');
    return data;
  },
  ausschliessen: async (
    userId: string,
    serverId: string,
    grund?: string,
  ): Promise<{ ok: boolean; meldung: string }> => {
    const { data } = await client.put(
      `/benutzer-schluessel/${userId}/ausschluss/${serverId}`,
      { grund },
    );
    return data;
  },
  ausschlussAufheben: async (
    userId: string,
    serverId: string,
  ): Promise<{ ok: boolean; meldung: string }> => {
    const { data } = await client.delete(
      `/benutzer-schluessel/${userId}/ausschluss/${serverId}`,
    );
    return data;
  },
  freigeben: async (
    userId: string,
    adresse: string,
    notiz?: string,
  ): Promise<{ ok: boolean; schritte: Array<{ name: string; ok?: boolean; meldung?: string }> }> => {
    const { data } = await client.post(`/benutzer-schluessel/${userId}/freigabe`, {
      adresse,
      notiz,
    });
    return data;
  },
  freigabeEntfernen: async (userId: string, id: string): Promise<{ ok: boolean }> => {
    const { data } = await client.delete(`/benutzer-schluessel/${userId}/freigabe/${id}`);
    return data;
  },

  // ── Selbstbedienung ──────────────────────────────────────────────────────
  //
  // Freigegeben wird genau die Adresse, von der die Anfrage kommt. Kein
  // Eingabefeld: sonst duerfte jeder Angemeldete eine beliebige Adresse durch
  // die Firewall lassen, auch eine fremde.

  /** Von welcher Adresse aus sitze ich gerade, und ist sie schon frei? */
  meineAdresse: async (): Promise<{
    adresse: string | null;
    freigegeben: boolean;
    eintrag: { id: string; notiz: string | null; erstelltAm: string } | null;
    /** Wohin eine Freigabe wirkt. `eingerichtet: false` heisst: dieser Proxy
     *  ist konfiguriert, aber unvollstaendig -- dort wirkt sie nicht. */
    ziele?: { name: string; url: string; eingerichtet: boolean }[];
  }> => {
    const { data } = await client.get('/benutzer-schluessel/mein/adresse');
    return data;
  },
  meineAdresseFreigeben: async (): Promise<{
    ok: boolean;
    adresse?: string;
    hinweis: string | null;
    schritte: Array<{ name: string; ok?: boolean; meldung?: string }>;
  }> => {
    const { data } = await client.post('/benutzer-schluessel/mein/freigabe');
    return data;
  },
  meineFreigabeEntfernen: async (id: string): Promise<{ ok: boolean }> => {
    const { data } = await client.delete(`/benutzer-schluessel/mein/freigabe/${id}`);
    return data;
  },
};
